import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "@/App";
import { useSearchParams } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle,
  Package,
  TrendingUp,
  Users,
  RefreshCw,
  Edit,
  UserPlus,
  DollarSign,
  XCircle,
  Calculator,
  CheckCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  History,
  Filter,
  Globe,
  Trash2,
  Weight,
  Pencil,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Printer,
  FileSpreadsheet,
  FileText,
  Calendar,
  CalendarDays,
  CalendarRange,
  Link2,
  CircleDollarSign,
  Hash,
  Shuffle,
  Ban,
  Loader2,
  Info,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import {
  exportToExcel,
  exportMultipleSheetsToExcel,
  prepareCostingDataForExport,
  prepareSalesDataForExport,
  prepareUnassignedDataForExport,
  generateHistoryPDF,
  openPrintPreview,
  getDateRangeLabel
} from "@/utils/exportUtils";
import ProductSuggestButton from "@/components/shipment-costing/ProductSuggestButton";
import QuickFixButton from "@/components/error-resolution/QuickFixButton";
// Note: DpClientProductFix and DpClientShippingFix removed from Costing - costing is same for all clients
// DP Client distinction only applies to Sales (different selling prices per client)
import SalesProductFix from "@/components/SalesProductFix";
import CostingExportDialog from "@/components/CostingExportDialog";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sort icon component - moved outside to prevent re-renders
const SortIcon = ({ sortKey, currentKey, direction }) => {
  if (currentKey !== sortKey) {
    return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
  }
  return direction === 'asc' 
    ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
    : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
};

// Sortable header component - moved outside to prevent re-renders
const SortableHeader = ({ column, label, className = "", sortConfig, onSort }) => (
  <th 
    className={`p-2 text-left cursor-pointer hover:bg-slate-100 select-none ${className}`}
    onClick={() => onSort(column)}
  >
    <div className="flex items-center">
      {label}
      <SortIcon sortKey={column} currentKey={sortConfig.key} direction={sortConfig.direction} />
    </div>
  </th>
);

const ErrorResolutionPage = () => {
  usePageTitle("Error Resolution");
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchParams] = useSearchParams();
  
  // Read tab from URL query param on mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["overview", "costing", "sales", "unassigned", "missing_tracking"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    // Deep-link from Ledger (Sales) error badges: prefill tracking search
    const searchParam = searchParams.get("search");
    if (searchParam) {
      setSearchTerm(searchParam);
      setActiveTab("costing");
    }
  }, [searchParams]);
  
  // Role-based permissions state
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  
  // Error lists
  const [costingErrors, setCostingErrors] = useState([]);
  const [salesErrors, setSalesErrors] = useState([]);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [resolutionHistory, setResolutionHistory] = useState([]);
  
  // Expanded rows for viewing products
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Expanded rows for unassigned orders tab
  const [expandedUnassignedRows, setExpandedUnassignedRows] = useState([]);
  
  // Filters
  const [filterClient, setFilterClient] = useState("");
  const [filterErrorType, setFilterErrorType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clientList, setClientList] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Export/Print dialogs
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogMode, setExportDialogMode] = useState("excel"); // "excel" or "print"
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalErrors, setTotalErrors] = useState(0);
  const pageSize = 50;
  
  // Resolution dialog
  const [resolveDialog, setResolveDialog] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [resolutionType, setResolutionType] = useState("");
  
  // New dialogs for sales errors
  const [editSellPriceDialog, setEditSellPriceDialog] = useState(false);
  const [linkSaleDialog, setLinkSaleDialog] = useState(false);
  const [ignoreDialog, setIgnoreDialog] = useState(false);
  const [newSellPrice, setNewSellPrice] = useState("");
  const [linkToTracking, setLinkToTracking] = useState("");
  const [ignoreReason, setIgnoreReason] = useState("");
  const [newValue, setNewValue] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [manualShipping, setManualShipping] = useState("");
  const [addToProducts, setAddToProducts] = useState(false);
  const [productPrice, setProductPrice] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [resolving, setResolving] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countryList, setCountryList] = useState([]);
  
  // Shipping service fix
  const [shippingServices, setShippingServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [serviceCountries, setServiceCountries] = useState([]);
  const [shippingRateLookup, setShippingRateLookup] = useState(null);
  const [lookingUpRate, setLookingUpRate] = useState(false);
  
  // Edit Sale Date dialog
  const [editSaleDateDialog, setEditSaleDateDialog] = useState(false);
  const [editQuantityDialog, setEditQuantityDialog] = useState(false);
  const [newQuantity, setNewQuantity] = useState("");
  const [newSaleDate, setNewSaleDate] = useState("");
  const [fetchingOrderDate, setFetchingOrderDate] = useState(false);
  
  // Bulk selection
  const [selectedItems, setSelectedItems] = useState([]);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkClientCode, setBulkClientCode] = useState("");
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  
  // Sync costs state
  const [syncingCosts, setSyncingCosts] = useState(false);
  
  // Auto-calculate DP Client sales state
  const [regeneratingDP, setRegeneratingDP] = useState(false);
  
  // Edit record dialog (for weight, qty, etc.)
  const [editRecordDialog, setEditRecordDialog] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [editFields, setEditFields] = useState({
    gross_weight: "",
    product_weight: "",
    medicine_cost: "",
    shipping_cost: "",
    qty: "",
    second_name: "",
    client_code: "",
    country: "",
    order_date: "",
    save_to_product_db: true
  });
  const [saving, setSaving] = useState(false);

  // Sorting state for tables
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Link to Order state (for sales resolution)
  const [linkOrderDialog, setLinkOrderDialog] = useState(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState("");
  const [orderSearchResults, setOrderSearchResults] = useState([]);
  const [searchingOrders, setSearchingOrders] = useState(false);
  const [selectedOrderToLink, setSelectedOrderToLink] = useState(null);
  
  // Manual Cost Entry state
  const [manualCostDialog, setManualCostDialog] = useState(false);
  const [manualCostValue, setManualCostValue] = useState("");
  const [saleToUpdate, setSaleToUpdate] = useState(null);
  
  // Narration dialogs
  const [narrationLogicDialog, setNarrationLogicDialog] = useState(false);
  const [narrationManualDialog, setNarrationManualDialog] = useState(false);
  const [narrationText, setNarrationText] = useState("");
  const [narrationSale, setNarrationSale] = useState(null);
  
  // History PDF Export dialog
  const [historyPdfDialog, setHistoryPdfDialog] = useState(false);
  const [historyPdfReportType, setHistoryPdfReportType] = useState("today");
  const [historyPdfPreview, setHistoryPdfPreview] = useState(false);
  const [historyPdfPreviewData, setHistoryPdfPreviewData] = useState(null);
  
  // Check if user is admin (can see sell prices)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isErrorManager = user?.role === 'error_manager';
  
  // Recheck errors state
  const [recheckingErrors, setRecheckingErrors] = useState(false);
  const [recheckType, setRecheckType] = useState(null); // 'shipping', 'products', 'unassigned', 'all'
  
  // Missing Tracking state
  const [missingTrackingParcels, setMissingTrackingParcels] = useState([]);
  const [missingTrackingPage, setMissingTrackingPage] = useState(1);
  const [missingTrackingTotal, setMissingTrackingTotal] = useState(0);
  const [missingTrackingTotalPages, setMissingTrackingTotalPages] = useState(1);
  const [missingTrackingLoading, setMissingTrackingLoading] = useState(false);
  const [missingTrackingFilter, setMissingTrackingFilter] = useState("");
  const [editTrackingDialog, setEditTrackingDialog] = useState(false);
  const [editTrackingRecord, setEditTrackingRecord] = useState(null);
  const [newTrackingValue, setNewTrackingValue] = useState("");
  const [savingTracking, setSavingTracking] = useState(false);
  const [deleteMissingDialog, setDeleteMissingDialog] = useState(false);
  const [deleteMissingPassword, setDeleteMissingPassword] = useState("");
  const [selectedMissingItems, setSelectedMissingItems] = useState([]);
  
  // Recheck errors function
  const handleRecheckErrors = async (type) => {
    setRecheckingErrors(true);
    setRecheckType(type);
    
    try {
      const endpoint = type === 'all' 
        ? '/api/errors/recheck/all'
        : `/api/errors/recheck/${type}`;
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date_from: dateFrom || null,
          date_to: dateTo || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (type === 'all') {
          toast.success(`Recheck complete! ${data.total_resolved} errors resolved.`);
        } else {
          toast.success(`${data.message || 'Recheck complete!'}`);
        }
        
        // Refresh current tab data
        if (activeTab === 'costing') fetchCostingErrors();
        else if (activeTab === 'sales') fetchSalesErrors();
        else if (activeTab === 'unassigned') fetchUnassignedOrders();
        else fetchDashboard();
      } else {
        toast.error(data.detail || 'Failed to recheck errors');
      }
    } catch (error) {
      console.error('Recheck error:', error);
      toast.error('Failed to recheck errors');
    } finally {
      setRecheckingErrors(false);
      setRecheckType(null);
    }
  };

  // Sort handler for client-side sorting (Costing, Unassigned tabs)
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Sort handler for server-side sorting (Sales tab)
  const handleSalesSort = (key) => {
    if (salesSortField === key) {
      // Toggle direction if same field
      setSalesSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSalesSortField(key);
      setSalesSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };
  
  // ============================================
  // EXPORT HANDLERS
  // ============================================
  
  // Get current filters object
  const getCurrentFilters = () => ({
    client: filterClient || 'All',
    errorType: filterErrorType || 'All',
    dateFrom: dateFrom || null,
    dateTo: dateTo || null
  });
  
  // Export Costing Errors as Product Price List template
  // (Product Name pre-filled — user fills price details and re-uploads to Product Prices panel)
  const handleExportProductTemplate = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterClient && filterClient !== "all") params.append("client_code", filterClient);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (searchTerm && searchTerm.length >= 2) params.append("search", searchTerm);

      const url = `${BACKEND_URL}/api/errors/costing/export-product-template${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Failed to export product template");
        return;
      }
      const count = resp.headers.get("X-Product-Count") || "?";
      const blob = await resp.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `Costing_Errors_Product_Template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success(`Exported ${count} unique product names in Product Price List template format.`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to export product template");
    } finally {
      setExporting(false);
    }
  };

  // Export Costing Errors as Shipping Price List template
  // (Shipment/Country/Weight Range pre-filled — user fills Cost + $ Price and re-uploads to Shipping Rates panel)
  const handleExportShippingTemplate = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterClient && filterClient !== "all") params.append("client_code", filterClient);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (searchTerm && searchTerm.length >= 2) params.append("search", searchTerm);

      const url = `${BACKEND_URL}/api/errors/costing/export-shipping-template${params.toString() ? `?${params.toString()}` : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Failed to export shipping template");
        return;
      }
      const count = resp.headers.get("X-Triple-Count") || "?";
      const blob = await resp.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `Costing_Errors_Shipping_Template_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success(`Exported ${count} unique shipping triples in Shipping Price List template format.`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to export shipping template");
    } finally {
      setExporting(false);
    }
  };

  // Pre-compute counts per Quick-Export category so the dropdown labels
  // can show row counts in brackets (e.g. "Product Issues Only (12)").
  // Prefer the server-wide totals from the dashboard summary; fall back to
  // current-page filtering so the labels stay useful even before dashboard
  // loads.
  const costingExportCounts = useMemo(() => {
    // With filters active, the global dashboard totals are misleading —
    // exports honour the filters, so label them accordingly.
    const filtersActive = !!(dateFrom || dateTo || filterClient || (searchTerm && searchTerm.length >= 2) || filterErrorType);
    const serverProd = dashboard?.summary?.total_costing_product_errors;
    const serverShip = dashboard?.summary?.total_costing_shipping_errors;
    if (!filtersActive && typeof serverProd === "number" && typeof serverShip === "number") {
      return { product: serverProd, shipping: serverShip, total: totalErrors, source: "server" };
    }
    if (filtersActive) {
      return { product: "filtered", shipping: "filtered", total: totalErrors, source: "filtered" };
    }
    const has = (e, ...words) =>
      e.errors?.some((err) => words.some((w) => err.includes(w))) || false;
    let prod = 0;
    let ship = 0;
    for (const e of costingErrors) {
      if (has(e, "Product", "Price")) prod += 1;
      if (has(e, "Shipping", "Service")) ship += 1;
    }
    return { product: prod, shipping: ship, total: costingErrors.length, source: "page" };
  }, [costingErrors, dashboard, totalErrors, dateFrom, dateTo, filterClient, searchTerm, filterErrorType]);

  // Export Costing Errors to Excel
  const handleExportCostingExcel = (exportType = 'all') => {
    setExporting(true);
    try {
      let dataToExport = sortData(costingErrors);
      let title = 'Costing Errors';
      
      // Filter by type if needed
      if (exportType === 'product') {
        dataToExport = dataToExport.filter(e => 
          e.errors?.some(err => err.includes('Product') || err.includes('Price'))
        );
        title = 'Costing Errors - Product Issues';
      } else if (exportType === 'shipping') {
        dataToExport = dataToExport.filter(e => 
          e.errors?.some(err => err.includes('Shipping') || err.includes('Service'))
        );
        title = 'Costing Errors - Shipping Issues';
      } else if (exportType === 'weight') {
        dataToExport = dataToExport.filter(e => 
          e.errors?.some(err => err.includes('Weight'))
        );
        title = 'Costing Errors - Weight Issues';
      } else if (exportType === 'country') {
        dataToExport = dataToExport.filter(e => 
          e.errors?.some(err => err.includes('Country'))
        );
        title = 'Costing Errors - Country Issues';
      }
      
      const { headers, rows, summaryStats } = prepareCostingDataForExport(dataToExport, getCurrentFilters());
      
      exportToExcel(rows, {
        fileName: `EAC_Costing_Errors_${exportType}`,
        sheetName: 'Costing Errors',
        headers,
        title,
        summaryStats,
        columnWidths: [20, 15, 30, 40, 12, 10, 8, 12]
      });
      
      toast.success(`Exported ${dataToExport.length} costing errors to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };
  
  // Export Sales Errors to Excel (fetch all data, respect role)
  const handleExportSalesExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL sales errors for export
      const params = new URLSearchParams({
        page: "1",
        page_size: "10000"
      });
      if (filterClient) params.set("client_code", filterClient);
      if (searchTerm && searchTerm.length >= 2) params.set("search", searchTerm);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (salesErrorType) params.set("error_type", salesErrorType);
      if (salesSortField) params.set("sort_field", salesSortField);
      if (salesSortOrder) params.set("sort_order", salesSortOrder);
      
      const response = await fetch(`${BACKEND_URL}/api/errors/sales?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allSalesErrors = data.errors || [];
        
        const { headers, rows, summaryStats } = prepareSalesDataForExport(
          sortData(allSalesErrors), 
          getCurrentFilters(),
          !isAdmin  // hideFinancials for error_manager
        );
        
        exportToExcel(rows, {
          fileName: 'EAC_Sales_Errors',
          sheetName: 'Sales Errors',
          headers,
          title: `Sales Errors - ${allSalesErrors.length} Unmatched Records`,
          summaryStats,
          columnWidths: !isAdmin ? [20, 15, 25, 15, 15, 12] : [20, 15, 25, 15, 15, 15, 15, 12]
        });
        
        toast.success(`Exported ${allSalesErrors.length} sales errors to Excel`);
      } else {
        toast.error("Failed to fetch data for export");
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };
  
  // Export Unassigned Orders to Excel (fetch all data)
  const handleExportUnassignedExcel = async () => {
    setExporting(true);
    try {
      // Fetch ALL unassigned orders for export
      const params = new URLSearchParams({
        page: "1",
        page_size: "10000"
      });
      
      const response = await fetch(`${BACKEND_URL}/api/errors/unassigned?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allUnassigned = data.orders || [];
        
        const { headers, rows, summaryStats } = prepareUnassignedDataForExport(sortData(allUnassigned), getCurrentFilters());
        
        exportToExcel(rows, {
          fileName: 'EAC_Unassigned_Orders',
          sheetName: 'Unassigned Orders',
          headers,
          title: `Unassigned Orders - ${allUnassigned.length} Records`,
          summaryStats,
          columnWidths: [20, 25, 15, 15, 12]
        });
        
        toast.success(`Exported ${allUnassigned.length} unassigned orders to Excel`);
      } else {
        toast.error("Failed to fetch data for export");
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };
  
  // Export All 3 Tabs to Single Excel (fetch all data, respect current tab)
  const handleExportAllToExcel = async () => {
    setExporting(true);
    try {
      // Fetch all data for each tab
      const [costingRes, salesRes, unassignedRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/errors/costing?page=1&page_size=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/errors/sales?page=1&page_size=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/errors/unassigned?page=1&page_size=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const costingData = costingRes.ok ? (await costingRes.json()).errors || [] : [];
      const salesData = salesRes.ok ? (await salesRes.json()).errors || [] : [];
      const unassignedData = unassignedRes.ok ? (await unassignedRes.json()).orders || [] : [];
      
      const costingExport = prepareCostingDataForExport(sortData(costingData), getCurrentFilters());
      const salesExport = prepareSalesDataForExport(sortData(salesData), getCurrentFilters(), !isAdmin);
      const unassignedExport = prepareUnassignedDataForExport(sortData(unassignedData), getCurrentFilters());
      
      exportMultipleSheetsToExcel([
        {
          data: costingExport.rows,
          sheetName: 'Costing Errors',
          headers: costingExport.headers,
          title: 'Costing Errors'
        },
        {
          data: salesExport.rows,
          sheetName: 'Sales Errors',
          headers: salesExport.headers,
          title: 'Sales Errors'
        },
        {
          data: unassignedExport.rows,
          sheetName: 'Unassigned Orders',
          headers: unassignedExport.headers,
          title: 'Unassigned Orders'
        }
      ], 'EAC_All_Errors');
      
      const totalRecords = costingData.length + salesData.length + unassignedData.length;
      toast.success(`Exported ${totalRecords} records to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };
  
  // Print Handlers
  const handlePrintCosting = (printType = 'all') => {
    let dataToExport = sortData(costingErrors);
    let title = 'Costing Errors';
    
    if (printType === 'product') {
      dataToExport = dataToExport.filter(e => 
        e.errors?.some(err => err.includes('Product') || err.includes('Price'))
      );
      title = 'Costing Errors - Product Issues';
    } else if (printType === 'shipping') {
      dataToExport = dataToExport.filter(e => 
        e.errors?.some(err => err.includes('Shipping') || err.includes('Service'))
      );
      title = 'Costing Errors - Shipping Issues';
    }
    
    const { headers, rows, summaryStats } = prepareCostingDataForExport(dataToExport, getCurrentFilters());
    
    openPrintPreview({
      title,
      subtitle: 'Error Resolution Panel',
      summaryStats,
      filters: getCurrentFilters(),
      tables: [{ title, headers, rows }]
    });
  };
  
  const handlePrintSales = async () => {
    try {
      setExporting(true);
      
      // Fetch ALL sales errors (not paginated) for printing
      const params = new URLSearchParams({
        page: "1",
        page_size: "10000"  // Get all records
      });
      if (filterClient) params.set("client_code", filterClient);
      if (searchTerm && searchTerm.length >= 2) params.set("search", searchTerm);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (salesErrorType) params.set("error_type", salesErrorType);
      if (salesSortField) params.set("sort_field", salesSortField);
      if (salesSortOrder) params.set("sort_order", salesSortOrder);
      
      const response = await fetch(`${BACKEND_URL}/api/errors/sales?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allSalesErrors = data.errors || [];
        
        // Prepare data for print - respect role-based column visibility
        const { headers, rows, summaryStats } = prepareSalesDataForExport(
          sortData(allSalesErrors), 
          getCurrentFilters(),
          !isAdmin  // hideFinancials flag for error_manager
        );
        
        openPrintPreview({
          title: 'Sales Errors',
          subtitle: `${allSalesErrors.length} Unmatched Sales Records${!isAdmin ? ' (Financial data hidden)' : ''}`,
          summaryStats: isAdmin ? summaryStats : summaryStats.filter(s => !['Sell Price', 'Profit'].includes(s.label)),
          filters: getCurrentFilters(),
          tables: [{ title: 'Sales Errors', headers, rows }]
        });
      } else {
        toast.error("Failed to fetch data for printing");
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print sales errors");
    } finally {
      setExporting(false);
    }
  };
  
  const handlePrintUnassigned = async () => {
    try {
      setExporting(true);
      
      // Fetch ALL unassigned orders for printing
      const params = new URLSearchParams({
        page: "1",
        page_size: "10000"
      });
      
      const response = await fetch(`${BACKEND_URL}/api/errors/unassigned?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allUnassigned = data.orders || [];
        
        const { headers, rows, summaryStats } = prepareUnassignedDataForExport(sortData(allUnassigned), getCurrentFilters());
        
        openPrintPreview({
          title: 'Unassigned Orders',
          subtitle: `${allUnassigned.length} Orders without Client Assignment`,
          summaryStats,
          filters: getCurrentFilters(),
          tables: [{ title: 'Unassigned Orders', headers, rows }]
        });
      } else {
        toast.error("Failed to fetch data for printing");
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print unassigned orders");
    } finally {
      setExporting(false);
    }
  };
  
  const handlePrintAll = async () => {
    try {
      setExporting(true);
      
      // Fetch all data for each tab
      const [costingRes, salesRes, unassignedRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/errors/costing?page=1&page_size=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/errors/sales?page=1&page_size=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/errors/unassigned?page=1&page_size=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const allCostingData = costingRes.ok ? (await costingRes.json()).errors || [] : [];
      const allSalesData = salesRes.ok ? (await salesRes.json()).errors || [] : [];
      const allUnassignedData = unassignedRes.ok ? (await unassignedRes.json()).orders || [] : [];
      
      const costingExport = prepareCostingDataForExport(sortData(allCostingData), getCurrentFilters());
      const salesExport = prepareSalesDataForExport(sortData(allSalesData), getCurrentFilters(), !isAdmin);
      const unassignedExport = prepareUnassignedDataForExport(sortData(allUnassignedData), getCurrentFilters());
      
      openPrintPreview({
        title: 'All Error Reports',
        subtitle: `Combined Report - ${allCostingData.length + allSalesData.length + allUnassignedData.length} Total Records`,
        summaryStats: [
          { label: 'Costing Errors', value: allCostingData.length },
          { label: 'Sales Errors', value: allSalesData.length },
          { label: 'Unassigned Orders', value: allUnassignedData.length },
          { label: 'Total', value: allCostingData.length + allSalesData.length + allUnassignedData.length }
        ],
        filters: getCurrentFilters(),
        tables: [
          { title: 'Costing Errors', headers: costingExport.headers, rows: costingExport.rows },
          { title: 'Sales Errors', headers: salesExport.headers, rows: salesExport.rows },
          { title: 'Unassigned Orders', headers: unassignedExport.headers, rows: unassignedExport.rows }
        ]
      });
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print all reports");
    } finally {
      setExporting(false);
    }
  };
  
  // History PDF Export - Show dialog first
  const handleExportHistoryPDF = (reportType) => {
    setHistoryPdfReportType(reportType);
    setHistoryPdfDialog(true);
  };
  
  // Prepare history data for preview/export
  const prepareHistoryReportData = (reportType) => {
    const now = new Date();
    const filteredData = resolutionHistory.filter(item => {
      const itemDate = new Date(item.timestamp);
      switch (reportType) {
        case 'today':
          return itemDate.toDateString() === now.toDateString();
        case 'weekly':
          const weekAgo = new Date(now);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return itemDate >= weekAgo;
        case 'monthly':
          const monthAgo = new Date(now);
          monthAgo.setDate(monthAgo.getDate() - 30);
          return itemDate >= monthAgo;
        default:
          return true;
      }
    });
    
    // Calculate summary stats
    const totalResolutions = filteredData.length;
    const uniqueUsers = [...new Set(filteredData.map(h => h.username))].length;
    const resolutionTypes = {};
    filteredData.forEach(h => {
      const type = h.data_accessed?.resolution_type || h.action || 'other';
      resolutionTypes[type] = (resolutionTypes[type] || 0) + 1;
    });
    
    const reportTypeLabel = reportType === 'today' ? "Today's Report" 
      : reportType === 'weekly' ? 'Weekly Report (Last 7 Days)' 
      : 'Monthly Report (Last 30 Days)';
    
    return {
      filteredData,
      totalResolutions,
      uniqueUsers,
      resolutionTypes,
      reportTypeLabel,
      generatedAt: now.toLocaleString()
    };
  };
  
  // Show screen preview for history PDF
  const handleHistoryScreenPreview = () => {
    const reportData = prepareHistoryReportData(historyPdfReportType);
    setHistoryPdfPreviewData(reportData);
    setHistoryPdfPreview(true);
    setHistoryPdfDialog(false);
  };
  
  // Direct PDF download for history
  const handleHistoryDirectPDF = () => {
    setExporting(true);
    try {
      generateHistoryPDF(resolutionHistory, historyPdfReportType);
      toast.success(`${historyPdfReportType.charAt(0).toUpperCase() + historyPdfReportType.slice(1)} report downloaded`);
      setHistoryPdfDialog(false);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  // Sort function for data
  const sortData = (data) => {
    if (!sortConfig.key || !data) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested values like errors array
      if (sortConfig.key === 'errors') {
        aVal = a.errors?.length || 0;
        bVal = b.errors?.length || 0;
      }
      
      // Handle product name (second_name or product)
      if (sortConfig.key === 'product') {
        aVal = a.second_name || a.product || '';
        bVal = b.second_name || b.product || '';
      }
      
      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Numeric comparison for costs/numbers
      if (typeof aVal === 'number' || !isNaN(parseFloat(aVal))) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String comparison (case-insensitive)
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (filterClient) params.set("client_code", filterClient);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const response = await fetch(`${BACKEND_URL}/api/errors/dashboard${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  }, [token, dateFrom, dateTo, filterClient]);

  // Fetch client list
  const fetchClientList = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/errors/client-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClientList(data.clients || []);
      }
    } catch (error) {
      console.error("Error fetching client list:", error);
    }
  }, [token]);

  // Fetch country list for shipping
  const fetchCountryList = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipping/countries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCountryList(data.countries || []);
      }
    } catch (error) {
      console.error("Error fetching country list:", error);
    }
  }, [token]);

  // Fetch costing errors (sequence-guarded: a stale slow response can never
  // overwrite a newer one — this was causing intermittent "no errors" flashes)
  const costingFetchSeq = useRef(0);
  const fetchCostingErrors = useCallback(async () => {
    const seq = ++costingFetchSeq.current;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      if (filterClient) params.set("client_code", filterClient);
      if (filterErrorType) params.set("error_type", filterErrorType);
      if (searchTerm && searchTerm.length >= 2) params.set("search", searchTerm);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      
      const response = await fetch(`${BACKEND_URL}/api/errors/costing?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (seq !== costingFetchSeq.current) return; // stale response — discard
      if (response.ok) {
        const data = await response.json();
        setCostingErrors(data.errors || []);
        setTotalPages(data.total_pages || 1);
        setTotalErrors(data.total || 0);
      } else {
        toast.error("Costing errors failed to load — retrying may help");
      }
    } catch (error) {
      console.error("Error fetching costing errors:", error);
      if (seq === costingFetchSeq.current) toast.error("Network error while loading costing errors");
    } finally {
      if (seq === costingFetchSeq.current) setLoading(false);
    }
  }, [token, currentPage, filterClient, filterErrorType, searchTerm, dateFrom, dateTo]);

  // Fetch sales errors
  // State for sales error type filter
  const [salesErrorType, setSalesErrorType] = useState("");
  
  // Count active filters (must be after salesErrorType is defined)
  const activeFilterCount = [
    filterClient !== "",
    filterErrorType !== "",
    searchTerm !== "",
    dateFrom !== "",
    dateTo !== "",
    salesErrorType !== ""
  ].filter(Boolean).length;
  
  // Sorting state for sales tab
  const [salesSortField, setSalesSortField] = useState("sale_date");
  const [salesSortOrder, setSalesSortOrder] = useState("desc");
  
  const fetchSalesErrors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      if (filterClient) params.set("client_code", filterClient);
      if (searchTerm && searchTerm.length >= 2) params.set("search", searchTerm);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (salesErrorType) params.set("error_type", salesErrorType);
      // Add sorting parameters for server-side sorting
      if (salesSortField) params.set("sort_field", salesSortField);
      if (salesSortOrder) params.set("sort_order", salesSortOrder);
      
      const response = await fetch(`${BACKEND_URL}/api/errors/sales?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSalesErrors(data.errors || []);
        setTotalPages(data.total_pages || 1);
        setTotalErrors(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching sales errors:", error);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, filterClient, searchTerm, dateFrom, dateTo, salesErrorType, salesSortField, salesSortOrder]);

  // Fetch unassigned orders
  const fetchUnassignedOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString()
      });
      
      // Add search filter
      if (searchTerm) params.set("search", searchTerm);
      
      // Add date filters
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      
      const response = await fetch(`${BACKEND_URL}/api/errors/unassigned?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnassignedOrders(data.orders || []);
        setTotalPages(data.total_pages || 1);
        setTotalErrors(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching unassigned orders:", error);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, searchTerm, dateFrom, dateTo]);

  // Fetch resolution history
  const fetchResolutionHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/resolution-history?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setResolutionHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch missing tracking parcels
  const fetchMissingTrackingParcels = useCallback(async () => {
    try {
      setMissingTrackingLoading(true);
      const params = new URLSearchParams({
        page: missingTrackingPage.toString(),
        page_size: "50"
      });
      if (missingTrackingFilter) params.set("client_code", missingTrackingFilter);
      
      const response = await fetch(`${BACKEND_URL}/api/errors/missing-tracking?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMissingTrackingParcels(data.parcels || []);
        setMissingTrackingTotal(data.total || 0);
        setMissingTrackingTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error("Error fetching missing tracking:", error);
    } finally {
      setMissingTrackingLoading(false);
    }
  }, [token, missingTrackingPage, missingTrackingFilter]);

  // Handle assign tracking
  const handleAssignTracking = async () => {
    if (!newTrackingValue.trim() || !editTrackingRecord) return;
    setSavingTracking(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/errors/missing-tracking/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ record_id: editTrackingRecord.record_id, tracking: newTrackingValue.trim() })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message);
        // Auto-recalculate costs if we inherited parent fields (so the
        // orphan rows pick up shipping/medicine costs against the now
        // known client/service/country).
        if (data.matched_existing_parcel && data.tracking) {
          try {
            await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-by-tracking`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ tracking_numbers: [data.tracking] }),
            });
            toast.success(`Recalculated costs for ${data.tracking}`);
          } catch (e) {
            // Recalc is best-effort; the user can trigger it manually from the costing page
            console.warn("Auto-recalc failed:", e);
          }
        }
        setEditTrackingDialog(false);
        setNewTrackingValue("");
        setEditTrackingRecord(null);
        fetchMissingTrackingParcels();
        fetchDashboard();
      } else {
        toast.error(data.detail || "Failed to update tracking");
      }
    } catch (err) {
      toast.error("Failed to update tracking");
    } finally {
      setSavingTracking(false);
    }
  };

  // Handle delete missing tracking parcels
  const handleDeleteMissingParcels = async () => {
    if (!deleteMissingPassword || selectedMissingItems.length === 0) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/errors/missing-tracking/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          record_ids: selectedMissingItems.map(p => p.record_id),
          password: deleteMissingPassword
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(data.message);
        setDeleteMissingDialog(false);
        setDeleteMissingPassword("");
        setSelectedMissingItems([]);
        fetchMissingTrackingParcels();
        fetchDashboard();
      } else {
        toast.error(data.detail || "Delete failed");
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  // Fetch role permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.role || user?.role === 'super_admin') {
        setLoadingPermissions(false);
        return; // Admin sees everything
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/role-permissions/${user.role}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setHiddenTabs(data.error_resolution?.hidden_tabs || []);
        }
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      } finally {
        setLoadingPermissions(false);
      }
    };
    
    fetchPermissions();
  }, [user?.role, token]);

  // Check if a tab is visible based on permissions
  const isTabVisible = (tabId) => !hiddenTabs.includes(tabId);

  // Initial load
  useEffect(() => {
    fetchDashboard();
    fetchClientList();
    fetchCountryList();
  }, [fetchDashboard, fetchClientList, fetchCountryList]);

  // Set default tab to first visible tab when permissions load
  useEffect(() => {
    if (!loadingPermissions && hiddenTabs.length > 0) {
      const allTabs = ['overview', 'costing', 'sales', 'unassigned', 'history'];
      const visibleTabs = allTabs.filter(tab => !hiddenTabs.includes(tab));
      if (visibleTabs.length > 0 && hiddenTabs.includes(activeTab)) {
        setActiveTab(visibleTabs[0]);
      }
    }
  }, [loadingPermissions, hiddenTabs, activeTab]);

  // Reset to page 1 only when tab changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedItems([]);
  }, [activeTab]);

  // Load data based on active tab and pagination
  useEffect(() => {
    if (activeTab === "costing") {
      fetchCostingErrors();
    } else if (activeTab === "sales") {
      fetchSalesErrors();
    } else if (activeTab === "unassigned") {
      fetchUnassignedOrders();
    } else if (activeTab === "history") {
      fetchResolutionHistory();
    } else if (activeTab === "missing_tracking") {
      fetchMissingTrackingParcels();
    }
  }, [activeTab, currentPage, filterClient, filterErrorType, searchTerm, dateFrom, dateTo, fetchCostingErrors, fetchSalesErrors, fetchUnassignedOrders, fetchResolutionHistory, fetchMissingTrackingParcels]);

  // Refresh missing tracking when its page/filter changes
  useEffect(() => {
    if (activeTab === "missing_tracking") {
      fetchMissingTrackingParcels();
    }
  }, [missingTrackingPage, missingTrackingFilter, fetchMissingTrackingParcels, activeTab]);

  // Open resolve dialog
  const openResolveDialog = (error, type) => {
    setSelectedError(error);
    setResolutionType(type);
    setNewValue("");
    setManualPrice("");
    setManualShipping("");
    setAddToProducts(false);
    setProductPrice("");
    setResolutionNotes("");
    setSelectedCountry("");
    setSelectedService("");
    setServiceCountries([]);
    setShippingRateLookup(null);
    setResolveDialog(true);
    
    // Fetch shipping services for assign_service type
    if (type === "assign_service") {
      fetchShippingServices(error);
    }
  };
  
  // Fetch available shipping services
  const fetchShippingServices = async (error) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/errors/shipping-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setShippingServices(data.services || []);
        
        // Auto-detect service from tracking
        const tracking = error?.tracking || "";
        if (tracking) {
          const upper = tracking.toUpperCase();
          let detectedService = "";
          if (upper.endsWith("AE") || upper.startsWith("LA") || upper.startsWith("LB")) detectedService = "USPS";
          else if (upper.startsWith("LP")) detectedService = "ITPS";
          else if (upper.startsWith("RM") || upper.startsWith("RA") || upper.startsWith("RB")) detectedService = "RMS";
          else if (upper.startsWith("EM") || upper.startsWith("EA") || upper.startsWith("EB")) detectedService = "EMS";
          
          if (detectedService) {
            setSelectedService(detectedService);
            const svc = data.services?.find(s => s.service === detectedService);
            if (svc) setServiceCountries(svc.countries || []);
          }
        }
        
        // Auto-set country from detected_country
        const detectedCountry = error?.detected_country || error?.country || "";
        if (detectedCountry) {
          setSelectedCountry(detectedCountry.toUpperCase());
        }
      }
    } catch (e) {
      console.error("Error fetching shipping services:", e);
    }
  };
  
  // Lookup shipping rate when service + country selected
  const lookupShippingRate = async (service, country, weight) => {
    if (!service || !country) return;
    try {
      setLookingUpRate(true);
      const response = await fetch(
        `${BACKEND_URL}/api/errors/shipping-rate-lookup?service=${encodeURIComponent(service)}&country=${encodeURIComponent(country)}&weight=${weight || 0}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setShippingRateLookup(data);
      }
    } catch (e) {
      console.error("Error looking up rate:", e);
    } finally {
      setLookingUpRate(false);
    }
  };

  // Handle resolution
  const handleResolve = async () => {
    if (!selectedError) return;
    
    try {
      setResolving(true);
      const tracking = selectedError.tracking || selectedError.tracking_number;
      
      const body = {
        resolution_type: resolutionType,
        notes: resolutionNotes
      };
      
      if (resolutionType === "edit_product" || resolutionType === "assign_client") {
        if (!newValue.trim()) {
          toast.error("Please enter a value");
          return;
        }
        body.new_value = newValue;
        
        // If adding to products list
        if (resolutionType === "edit_product" && addToProducts) {
          if (!productPrice || parseFloat(productPrice) <= 0) {
            toast.error("Please enter a valid price for the new product");
            return;
          }
          body.add_to_products = true;
          body.product_price = parseFloat(productPrice);
        }
      }
      
      if (resolutionType === "assign_country") {
        if (!selectedCountry) {
          toast.error("Please select a country");
          return;
        }
        body.country = selectedCountry;
      }
      
      if (resolutionType === "manual_price") {
        if (manualPrice) body.manual_price = parseFloat(manualPrice);
        if (manualShipping) body.manual_shipping = parseFloat(manualShipping);
      }
      
      if (resolutionType === "assign_service") {
        if (!selectedService) {
          toast.error("Please select a shipping service");
          return;
        }
        if (!selectedCountry) {
          toast.error("Please select a country");
          return;
        }
        body.new_value = selectedService;
        body.country = selectedCountry;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/errors/resolve/${tracking}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Error resolved successfully");
        setResolveDialog(false);
        fetchDashboard();
        
        // Refresh current list
        if (activeTab === "costing") fetchCostingErrors();
        else if (activeTab === "sales") fetchSalesErrors();
        else if (activeTab === "unassigned") fetchUnassignedOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to resolve error");
      }
    } catch (error) {
      console.error("Resolution error:", error);
      toast.error("Failed to resolve error");
    } finally {
      setResolving(false);
    }
  };

  // ============================================
  // SALES ERROR RESOLUTION HANDLERS
  // ============================================
  
  // Open Link to Order dialog
  const openLinkOrderDialog = (sale) => {
    setSaleToUpdate(sale);
    setOrderSearchTerm("");
    setOrderSearchResults([]);
    setSelectedOrderToLink(null);
    setLinkOrderDialog(true);
  };
  
  // Search for orders to link
  const searchOrdersToLink = async () => {
    if (!orderSearchTerm || orderSearchTerm.length < 3) {
      toast.error("Enter at least 3 characters to search");
      return;
    }
    
    try {
      setSearchingOrders(true);
      const response = await fetch(
        `${BACKEND_URL}/api/shipment-costing/search-orders?query=${encodeURIComponent(orderSearchTerm)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setOrderSearchResults(data.orders || []);
        if (data.orders?.length === 0) {
          toast.info("No matching orders found");
        }
      } else {
        toast.error("Search failed");
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed");
    } finally {
      setSearchingOrders(false);
    }
  };
  
  // Link sale to selected order
  const handleLinkToOrder = async () => {
    if (!saleToUpdate || !selectedOrderToLink) {
      toast.error("Please select an order to link");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/link-sale-to-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          sale_tracking: saleToUpdate.tracking || saleToUpdate.tracking_number,
          order_tracking: selectedOrderToLink.tracking,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Sale linked to order successfully");
        setLinkOrderDialog(false);
        setSaleToUpdate(null);
        setSelectedOrderToLink(null);
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to link sale");
      }
    } catch (error) {
      console.error("Link error:", error);
      toast.error("Failed to link sale to order");
    } finally {
      setResolving(false);
    }
  };
  
  // Open Manual Cost Entry dialog
  const openManualCostDialog = (sale) => {
    setSaleToUpdate(sale);
    setManualCostValue(sale.cost_price?.toString() || "");
    setResolutionNotes("");
    setManualCostDialog(true);
  };
  
  // Handle Manual Cost Entry
  const handleManualCostEntry = async () => {
    if (!saleToUpdate) return;
    
    const costValue = parseFloat(manualCostValue);
    if (isNaN(costValue) || costValue < 0) {
      toast.error("Please enter a valid cost amount");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/update-sale-cost`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tracking: saleToUpdate.tracking || saleToUpdate.tracking_number,
          cost_price: costValue,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Cost updated successfully");
        setManualCostDialog(false);
        setSaleToUpdate(null);
        setManualCostValue("");
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update cost");
      }
    } catch (error) {
      console.error("Cost update error:", error);
      toast.error("Failed to update cost");
    } finally {
      setResolving(false);
    }
  };

  // Handle Edit Sell Price (for negative profit)
  const handleEditSellPrice = async () => {
    if (!selectedError) return;
    
    const sellValue = parseFloat(newSellPrice);
    if (isNaN(sellValue) || sellValue < 0) {
      toast.error("Please enter a valid sell price");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/update-sale-price`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tracking: selectedError.tracking || selectedError.tracking_number,
          sell_price: sellValue,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Sell price updated successfully");
        setEditSellPriceDialog(false);
        setSelectedError(null);
        setNewSellPrice("");
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update sell price");
      }
    } catch (error) {
      console.error("Sell price update error:", error);
      toast.error("Failed to update sell price");
    } finally {
      setResolving(false);
    }
  };

  // Handle Edit Sale Date
  const handleEditSaleDate = async () => {
    if (!selectedError) return;
    
    if (!newSaleDate) {
      toast.error("Please enter a valid date");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/update-sale-date`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tracking: selectedError.tracking || selectedError.tracking_number,
          sale_date: newSaleDate,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Sale date updated successfully");
        setEditSaleDateDialog(false);
        setSelectedError(null);
        setNewSaleDate("");
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update sale date");
      }
    } catch (error) {
      console.error("Sale date update error:", error);
      toast.error("Failed to update sale date");
    } finally {
      setResolving(false);
    }
  };

  // Handle Edit Quantity
  const handleEditQuantity = async () => {
    if (!selectedError) return;
    const qty = parseInt(newQuantity);
    if (!qty || qty < 1) {
      toast.error("Please enter a valid quantity (minimum 1)");
      return;
    }
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/update-quantity`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tracking: selectedError.tracking || selectedError.tracking_number,
          quantity: qty,
          notes: resolutionNotes
        })
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Quantity updated successfully");
        setEditQuantityDialog(false);
        setSelectedError(null);
        setNewQuantity("");
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update quantity");
      }
    } catch (error) {
      toast.error("Failed to update quantity");
    } finally {
      setResolving(false);
    }
  };


  // Fetch date from shipment order
  const fetchDateFromShipmentOrder = async () => {
    if (!selectedError) return;
    
    const tracking = selectedError.tracking || selectedError.tracking_number;
    if (!tracking) {
      toast.error("No tracking number available");
      return;
    }
    
    try {
      setFetchingOrderDate(true);
      const response = await fetch(
        `${BACKEND_URL}/api/shipment-costing/order/${encodeURIComponent(tracking)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const order = await response.json();
        const orderDate = order.order_date;
        if (orderDate && orderDate.length === 6) {
          // Convert DDMMYY to YYYY-MM-DD
          const day = orderDate.substring(0, 2);
          const month = orderDate.substring(2, 4);
          const year = orderDate.substring(4, 6);
          const isoDate = `20${year}-${month}-${day}`;
          setNewSaleDate(isoDate);
          toast.success(`Date fetched from shipment order: ${isoDate}`);
        } else if (orderDate) {
          setNewSaleDate(orderDate);
          toast.success(`Date fetched: ${orderDate}`);
        } else {
          toast.warning("No date found in shipment order");
        }
      } else {
        toast.error("Order not found in shipment costing");
      }
    } catch (error) {
      console.error("Fetch order date error:", error);
      toast.error("Failed to fetch order date");
    } finally {
      setFetchingOrderDate(false);
    }
  };

  // Handle Link Sale to Sale
  const handleLinkSaleToSale = async () => {
    if (!selectedError || !linkToTracking) {
      toast.error("Please enter a tracking number to link to");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/link-sale-to-sale`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          source_tracking: selectedError.tracking || selectedError.tracking_number,
          target_tracking: linkToTracking,
          merge_amounts: true,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Sales linked successfully");
        setLinkSaleDialog(false);
        setSelectedError(null);
        setLinkToTracking("");
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to link sales");
      }
    } catch (error) {
      console.error("Link sale error:", error);
      toast.error("Failed to link sales");
    } finally {
      setResolving(false);
    }
  };

  // Handle Ignore Sale Error
  const handleIgnoreSale = async () => {
    if (!selectedError) return;
    
    if (!ignoreReason) {
      toast.error("Please provide a reason for ignoring");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/ignore-sale`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tracking: selectedError.tracking || selectedError.tracking_number,
          reason: ignoreReason,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || "Sale error ignored");
        setIgnoreDialog(false);
        setSelectedError(null);
        setIgnoreReason("");
        setResolutionNotes("");
        fetchDashboard();
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to ignore sale");
      }
    } catch (error) {
      console.error("Ignore error:", error);
      toast.error("Failed to ignore sale error");
    } finally {
      setResolving(false);
    }
  };

  // Handle Add Narration (Logic or Manual)
  const handleAddNarration = async (type) => {
    if (!narrationSale || !narrationText.trim()) {
      toast.error("Please enter narration text");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/add-narration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tracking: narrationSale.tracking || narrationSale.tracking_number,
          narration_type: type,
          narration_text: narrationText,
          notes: resolutionNotes
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || `${type.charAt(0).toUpperCase() + type.slice(1)} narration added successfully`);
        
        // Close the appropriate dialog
        if (type === "logic") {
          setNarrationLogicDialog(false);
        } else {
          setNarrationManualDialog(false);
        }
        
        setNarrationSale(null);
        setNarrationText("");
        setResolutionNotes("");
        
        // Refresh data
        fetchSalesErrors();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add narration");
      }
    } catch (error) {
      console.error("Narration error:", error);
      toast.error("Failed to add narration");
    } finally {
      setResolving(false);
    }
  };

  // Handle bulk assign
  const handleBulkAssign = async () => {
    if (!bulkClientCode || selectedItems.length === 0) {
      toast.error("Please select items and client code");
      return;
    }
    
    try {
      setResolving(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/bulk-assign-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          tracking_numbers: selectedItems,
          client_code: bulkClientCode
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Assigned client to ${result.modified} orders`);
        setBulkAssignDialog(false);
        setSelectedItems([]);
        fetchDashboard();
        fetchUnassignedOrders();
      } else {
        toast.error("Bulk assign failed");
      }
    } catch (error) {
      toast.error("Bulk assign failed");
    } finally {
      setResolving(false);
    }
  };

  // Open delete dialog
  const openDeleteDialog = (record) => {
    setRecordToDelete(record);
    setDeleteDialog(true);
  };

  // Handle delete record
  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;
    if (!deletePassword.trim()) {
      toast.error("Please enter your password");
      return;
    }
    
    try {
      setDeleting(true);
      // Use record_id as query param for records without tracking
      const tracking = recordToDelete.tracking || "_no_tracking_";
      const params = new URLSearchParams();
      if (recordToDelete.record_id) {
        params.append("record_id", recordToDelete.record_id);
      }
      params.append("password", deletePassword);
      const queryStr = params.toString() ? `?${params.toString()}` : "";
      const response = await fetch(
        `${BACKEND_URL}/api/errors/delete-record/${encodeURIComponent(tracking)}${queryStr}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setDeleteDialog(false);
        setRecordToDelete(null);
        setDeletePassword("");
        fetchDashboard();
        if (activeTab === "costing") fetchCostingErrors();
        else if (activeTab === "sales") fetchSalesErrors();
        else if (activeTab === "unassigned") fetchUnassignedOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Delete failed");
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error("Please select items to delete");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} record(s)? This cannot be undone.`)) {
      return;
    }
    
    try {
      setDeleting(true);
      const response = await fetch(`${BACKEND_URL}/api/errors/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(selectedItems)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setSelectedItems([]);
        fetchDashboard();
        if (activeTab === "costing") fetchCostingErrors();
        else if (activeTab === "sales") fetchSalesErrors();
        else if (activeTab === "unassigned") fetchUnassignedOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Bulk delete failed");
      }
    } catch (error) {
      toast.error("Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  // Open edit record dialog
  const openEditRecordDialog = (record) => {
    setRecordToEdit(record);
    setEditFields({
      gross_weight: record.gross_weight || record.parcel_gross_weight || "",
      product_weight: record.product_weight || "",
      medicine_cost: record.medicine_cost || "",
      shipping_cost: record.shipping_cost || "",
      qty: record.qty || "",
      second_name: record.second_name || record.product || "",
      client_code: record.client_code || "",
      country: record.country || "",
      order_date: record.order_date || "",
      save_to_product_db: true
    });
    setEditRecordDialog(true);
  };

  // Handle save edited record
  const handleSaveRecord = async () => {
    if (!recordToEdit) return;
    
    try {
      setSaving(true);
      
      // Only send non-empty changed fields
      const updateData = {};
      if (editFields.gross_weight !== "") updateData.gross_weight = parseFloat(editFields.gross_weight);
      if (editFields.product_weight !== "") updateData.product_weight = parseFloat(editFields.product_weight);
      if (editFields.medicine_cost !== "") updateData.medicine_cost = parseFloat(editFields.medicine_cost);
      if (editFields.shipping_cost !== "") updateData.shipping_cost = parseFloat(editFields.shipping_cost);
      if (editFields.qty !== "") updateData.qty = parseInt(editFields.qty);
      if (editFields.second_name) updateData.second_name = editFields.second_name;
      if (editFields.client_code) updateData.client_code = editFields.client_code;
      if (editFields.country) updateData.country = editFields.country;
      if (editFields.order_date) updateData.order_date = editFields.order_date;
      if (editFields.product_weight !== "") updateData.save_to_product_db = editFields.save_to_product_db;
      
      const response = await fetch(
        `${BACKEND_URL}/api/errors/update-record/${encodeURIComponent(recordToEdit.tracking)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        let message = result.message;
        if (result.auto_synced) {
          message += " (Auto-synced to Sales)";
        }
        if (result.product_weight_synced) {
          toast.success(message + " - Product weight saved to Product Prices DB");
        } else if (updateData.product_weight && updateData.save_to_product_db) {
          toast.warning(message + " - Could not find matching product in Product Prices DB");
        } else {
          toast.success(message);
        }
        setEditRecordDialog(false);
        setRecordToEdit(null);
        fetchDashboard();
        if (activeTab === "costing") fetchCostingErrors();
        else if (activeTab === "sales") fetchSalesErrors();
        else if (activeTab === "unassigned") fetchUnassignedOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Update failed");
      }
    } catch (error) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  // Toggle item selection
  const toggleSelection = (tracking) => {
    setSelectedItems(prev => 
      prev.includes(tracking) 
        ? prev.filter(t => t !== tracking)
        : [...prev, tracking]
    );
  };

  // Select all visible
  const selectAll = () => {
    const allTrackings = (activeTab === "unassigned" ? unassignedOrders : costingErrors)
      .map(e => e.tracking)
      .filter(Boolean);
    setSelectedItems(allTrackings);
  };

  // Render error type badge and get all errors formatted
  // Note: Costing is SAME for all clients - no DP Client distinction needed here
  // DP Client distinction only applies to Sales (different selling prices)
  const renderErrors = (errors, errorRecord = null) => {
    if (!errors || errors.length === 0) return null;
    
    // Get all errors as a formatted string for tooltip
    const allErrorsText = errors.join('\n');
    
    // Determine badge type based on error content
    const hasProductError = errors.some(e => e.includes("Product not found") || e.includes("Price Missing"));
    const hasShippingError = errors.some(e => e.includes("Shipping") || e.includes("Service"));
    const hasWeightError = errors.some(e => e.includes("Weight"));
    const hasCountryError = errors.some(e => e.includes("Country"));
    
    // Create badges array - NO DP Client badge in costing (costing is same for all)
    const badges = [];
    if (hasProductError) badges.push(<Badge key="product" variant="destructive" className="text-xs">Product</Badge>);
    if (hasShippingError) badges.push(<Badge key="shipping" variant="outline" className="bg-orange-100 text-orange-700 text-xs">Shipping</Badge>);
    if (hasWeightError) badges.push(<Badge key="weight" variant="outline" className="bg-yellow-100 text-yellow-700 text-xs">Weight</Badge>);
    if (hasCountryError) badges.push(<Badge key="country" variant="outline" className="bg-blue-100 text-blue-700 text-xs">Country</Badge>);
    
    // If no specific badge matched, show count
    if (badges.length === 0) {
      badges.push(<Badge key="count" variant="secondary" className="text-xs">{errors.length} issues</Badge>);
    }
    
    return (
      <div className="flex flex-col gap-1 cursor-help" title={allErrorsText}>
        <div className="flex items-center gap-1 flex-wrap">
          {badges}
          <span className="text-xs text-slate-500">({errors.length})</span>
        </div>
        <div className="text-xs text-slate-600 max-w-[280px]">
          {errors.map((err, i) => (
            <div key={i} className="flex items-center gap-1 truncate" title={err}>
              <span className="truncate">• {err.length > 30 ? err.substring(0, 30) + '...' : err}</span>
              
              {/* Product not found errors - use universal ProductSuggestButton for ALL clients */}
              {err.includes('Product not found') && (
                <>
                  <ProductSuggestButton 
                    errorText={err} 
                    token={token} 
                    onFixed={() => { fetchDashboard(); fetchCostingErrors(); }} 
                  />
                  <QuickFixButton
                    mode="product"
                    token={token}
                    defaultValues={{
                      product_name: errorRecord?.second_name || errorRecord?.product || (err.split(':').slice(1).join(':') || '').trim(),
                      second_name: errorRecord?.second_name || '',
                      tracking: errorRecord?.tracking || '',
                      client_code: errorRecord?.client_code || '',
                    }}
                    onFixed={() => { fetchDashboard(); fetchCostingErrors(); }}
                  />
                </>
              )}
              
              {/* Missing medicine cost / Price Missing errors - use universal ProductSuggestButton */}
              {(err.includes('Missing medicine cost') || err.includes('Price Missing')) && !err.includes('Product not found') && (
                <>
                  <ProductSuggestButton 
                    errorText={err} 
                    token={token} 
                    onFixed={() => { fetchDashboard(); fetchCostingErrors(); }} 
                  />
                  <QuickFixButton
                    mode="product"
                    token={token}
                    defaultValues={{
                      product_name: errorRecord?.second_name || errorRecord?.product || (err.split(':').slice(1).join(':') || '').trim(),
                      second_name: errorRecord?.second_name || '',
                      tracking: errorRecord?.tracking || '',
                      client_code: errorRecord?.client_code || '',
                    }}
                    onFixed={() => { fetchDashboard(); fetchCostingErrors(); }}
                  />
                </>
              )}

              {/* Shipping/Service errors — new Quick Add Shipping Rate */}
              {(err.includes('Shipping Rate Missing') || err.includes('Service Not Found') || err.includes('Country') || err.includes('Weight slab')) && (
                <QuickFixButton
                  mode="shipping"
                  token={token}
                  defaultValues={(() => {
                    const w = errorRecord?.gross_weight || errorRecord?.parcel_gross_weight || errorRecord?.weight || 0;
                    let wr = '';
                    if (w > 0) {
                      const upper = Math.ceil(w / 250) * 250 || 250;
                      const lower = upper > 250 ? upper - 249 : 0;
                      const fmt = (n) => n >= 1000 ? n.toLocaleString('en-US') : String(n);
                      wr = `${fmt(lower)}-${fmt(upper)}G`;
                    }
                    let country = (errorRecord?.detected_country || errorRecord?.country || '').toString();
                    if (country.includes('(') && country.endsWith(')')) {
                      country = country.split('(')[0].trim();
                    }
                    return {
                      shipment: (errorRecord?.detected_service || errorRecord?.service || '').toUpperCase(),
                      country: country.toUpperCase(),
                      weight_range: wr,
                      // Surface tracking + client in the dialog so the user
                      // can mentally tie the dialog to the row they clicked.
                      tracking: errorRecord?.tracking || '',
                      client_code: errorRecord?.client_code || '',
                    };
                  })()}
                  onFixed={() => { fetchDashboard(); fetchCostingErrors(); }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 sm:w-7 sm:h-7 text-amber-500" />
            Error Resolution Panel
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">
            Resolve errors across Shipment Costing, Sales, and Ledger modules
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sync Button for syncing fixed errors to Sales */}
          <Button 
            onClick={async () => {
              try {
                toast.info("Syncing costs to Sales...");
                const response = await fetch(`${BACKEND_URL}/api/sync/bulk`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                  },
                  body: JSON.stringify({ sync_type: "sales" })
                });
                const data = await response.json();
                if (response.ok) {
                  toast.success(`Synced ${data.sales_updated} records to Sales`);
                  fetchDashboard();
                } else {
                  toast.error(data.detail || "Sync failed");
                }
              } catch (error) {
                toast.error("Sync failed");
              }
            }}
            variant="outline"
            size="sm"
            className="gap-1.5 border-green-300 text-green-700 hover:bg-green-50 text-xs sm:text-sm"
            title="Sync all fixed costs to Sales panel"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sync to Sales</span>
            <span className="sm:hidden">Sync</span>
          </Button>
          {/* Smart Export Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs sm:text-sm" disabled={exporting} data-testid="export-all-btn">
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Export</span>
                <span className="sm:hidden">Export</span>
                <ChevronDown className="w-3 h-3 ml-0.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {/* Context-aware: Current Tab Export */}
              {activeTab !== "overview" && activeTab !== "history" && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Current View{filterClient ? ` (${filterClient})` : ""}
                  </DropdownMenuLabel>
                  {activeTab === "costing" && (
                    <DropdownMenuItem onClick={() => handleExportCostingExcel(filterClient ? 'filtered' : 'all')} data-testid="export-current-costing">
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-orange-500" />
                      Export Costing{filterClient ? ` — ${filterClient}` : ""} ({dashboard?.summary?.total_costing_errors || 0})
                    </DropdownMenuItem>
                  )}
                  {activeTab === "sales" && (
                    <DropdownMenuItem onClick={handleExportSalesExcel} data-testid="export-current-sales">
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-purple-500" />
                      Export Sales{filterClient ? ` — ${filterClient}` : ""} ({dashboard?.summary?.total_sales_errors || 0})
                    </DropdownMenuItem>
                  )}
                  {activeTab === "unassigned" && (
                    <DropdownMenuItem onClick={handleExportUnassignedExcel} data-testid="export-current-unassigned">
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-blue-500" />
                      Export Unassigned ({dashboard?.summary?.unassigned_orders || 0})
                    </DropdownMenuItem>
                  )}
                  {activeTab === "missing_tracking" && (
                    <DropdownMenuItem onClick={() => handleExportCostingExcel('missing_tracking')} data-testid="export-current-notracking">
                      <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" />
                      Export No Tracking ({dashboard?.summary?.missing_tracking || 0})
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                </>
              )}
              
              {/* Individual Tab Exports */}
              <DropdownMenuLabel className="text-xs text-muted-foreground">Export by Category</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExportCostingExcel('all')} data-testid="export-all-costing">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-orange-500" />
                All Costing Errors ({dashboard?.summary?.total_costing_errors || 0})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportSalesExcel} data-testid="export-all-sales">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-purple-500" />
                All Sales Errors ({dashboard?.summary?.total_sales_errors || 0})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportUnassignedExcel} data-testid="export-all-unassigned">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-blue-500" />
                All Unassigned ({dashboard?.summary?.unassigned_orders || 0})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportCostingExcel('missing_tracking')} data-testid="export-all-notracking">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-red-500" />
                All No Tracking ({dashboard?.summary?.missing_tracking || 0})
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              
              {/* Export Everything */}
              <DropdownMenuItem onClick={handleExportAllToExcel} data-testid="export-all-excel" className="font-medium">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                Export All Errors (Combined)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handlePrintAll} data-testid="print-all">
                <Printer className="w-4 h-4 mr-2 text-slate-600" />
                Print All Panels
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={async () => {
            const ok = window.confirm("Scan all shipment_orders for parcels with ship_cost=0 + service + country that aren't in shipping_prices, and flag them as 'Shipping Rate Missing' so they appear here?\n\n(This is non-destructive — already-flagged rows are skipped.)");
            if (!ok) return;
            try {
              const token = localStorage.getItem("token");
              toast.info("Scanning for missing-shipping parcels…");
              const r = await fetch(`${BACKEND_URL}/api/errors/backfill-missing-shipping-errors?dry_run=false`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
              });
              const d = await r.json();
              if (!r.ok) {
                toast.error(d.detail || `Backfill failed (HTTP ${r.status})`);
                return;
              }
              toast.success(`✅ Flagged ${d.flagged} new parcels · scanned ${d.scanned} · ${d.valid_combo_count} valid shipping combos in price table.`, { duration: 8000 });
              fetchDashboard();
              if (activeTab === "costing") fetchCostingErrors();
            } catch (e) {
              toast.error(`Backfill error: ${e.message}`);
            }
          }} variant="outline" size="sm" className="gap-1.5 text-xs sm:text-sm border-amber-300 text-amber-700 hover:bg-amber-50"
             data-testid="backfill-missing-shipping-btn"
             title="Surface legacy parcels with ship_cost=0 (missing country rate) as 'Shipping Rate Missing' errors">
            🛠 Find Missing Shipping
          </Button>
          <Button onClick={() => {
            fetchDashboard();
            if (activeTab === 'costing') fetchCostingErrors();
            else if (activeTab === 'sales') fetchSalesErrors();
            else if (activeTab === 'unassigned') fetchUnassignedOrders();
            else if (activeTab === 'history') fetchResolutionHistory();
            else if (activeTab === 'missing_tracking') fetchMissingTrackingParcels();
          }} variant="outline" size="sm" className="gap-1.5 text-xs sm:text-sm">
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab("overview")}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-[10px] sm:text-sm">Total Errors</p>
                  <p className="text-xl sm:text-3xl font-bold">{dashboard.summary?.total_errors || 0}</p>
                </div>
                <AlertTriangle className="w-6 h-6 sm:w-10 sm:h-10 text-red-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab("costing")}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-[10px] sm:text-sm">Costing Errors</p>
                  <p className="text-xl sm:text-3xl font-bold">{dashboard.summary?.total_costing_errors || 0}</p>
                </div>
                <Package className="w-6 h-6 sm:w-10 sm:h-10 text-orange-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab("sales")}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-[10px] sm:text-sm">Sales Errors</p>
                  <p className="text-xl sm:text-3xl font-bold">{dashboard.summary?.total_sales_errors || 0}</p>
                </div>
                <TrendingUp className="w-6 h-6 sm:w-10 sm:h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab("unassigned")}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-[10px] sm:text-sm">Unassigned Orders</p>
                  <p className="text-xl sm:text-3xl font-bold">{dashboard.summary?.unassigned_orders || 0}</p>
                </div>
                <Users className="w-6 h-6 sm:w-10 sm:h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab("history")}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-[10px] sm:text-sm">Pending Approvals</p>
                  <p className="text-xl sm:text-3xl font-bold">{dashboard.summary?.pending_approvals || 0}</p>
                </div>
                <CheckCircle className="w-6 h-6 sm:w-10 sm:h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveTab("missing_tracking")}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-rose-100 text-[10px] sm:text-sm">No Tracking</p>
                  <p className="text-xl sm:text-3xl font-bold">{dashboard.summary?.missing_tracking || 0}</p>
                  <p className="text-rose-200 text-[9px] sm:text-xs mt-0.5">Cost: ₹{(dashboard.summary?.missing_tracking_cost || 0).toLocaleString()}</p>
                </div>
                <AlertTriangle className="w-6 h-6 sm:w-10 sm:h-10 text-rose-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Errors by Client (Quick View) */}
      {dashboard && activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card>
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-lg flex items-center gap-2">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                Costing Errors by Client
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dashboard.costing_by_client?.length > 0 ? (
                  dashboard.costing_by_client.map((c, i) => (
                    <div 
                      key={i} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer gap-2"
                      onClick={() => {
                        setFilterClient(c.client_code);
                        setActiveTab("costing");
                      }}
                    >
                      <span className="font-medium text-xs sm:text-sm truncate">{c.client_code}</span>
                      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        <Badge variant="destructive" className="text-[10px] sm:text-xs">{c.error_count}</Badge>
                        <span className="text-[10px] sm:text-xs text-slate-500">{c.unique_orders} orders</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4 text-sm">No costing errors!</p>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-lg flex items-center gap-2">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500" />
                Sales Errors - Partners/Agents
                <Badge className="bg-indigo-100 text-indigo-700 text-[10px] ml-auto">{dashboard?.partner_error_count?.toLocaleString() || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dashboard.sales_by_partner?.length > 0 ? (
                  dashboard.sales_by_partner.map((c, i) => (
                    <div 
                      key={i} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer gap-2"
                      onClick={() => {
                        setFilterClient(c.client_code);
                        setActiveTab("sales");
                      }}
                    >
                      <span className="font-medium text-xs sm:text-sm truncate">{c.client_code}</span>
                      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        <Badge className="bg-indigo-100 text-indigo-700 text-[10px] sm:text-xs">{c.error_count}</Badge>
                        <span className="text-[10px] sm:text-xs text-slate-500">{c.unique_orders} orders</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4 text-sm">No partner sales errors!</p>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-lg flex items-center gap-2">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-teal-500" />
                Sales Errors - DP Clients
                <Badge className="bg-teal-100 text-teal-700 text-[10px] ml-auto">{dashboard?.dp_client_error_count?.toLocaleString() || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {dashboard.sales_by_dp_client?.length > 0 ? (
                  dashboard.sales_by_dp_client.map((c, i) => (
                    <div 
                      key={i} 
                      className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer gap-2"
                      onClick={() => {
                        setFilterClient(c.client_code);
                        setActiveTab("sales");
                      }}
                    >
                      <span className="font-medium text-xs sm:text-sm truncate">{c.client_code}</span>
                      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        <Badge className="bg-teal-100 text-teal-700 text-[10px] sm:text-xs">{c.error_count}</Badge>
                        <span className="text-[10px] sm:text-xs text-slate-500">{c.unique_orders} orders</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-500 text-center py-4 text-sm">No DP client sales errors!</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for Different Error Types */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="flex flex-wrap h-auto gap-1 w-full">
              {isTabVisible('overview') && (
                <TabsTrigger value="overview" className="gap-1.5 text-xs sm:text-sm flex-1 min-w-[80px]" data-testid="tab-overview">
                  <AlertTriangle className="w-3.5 h-3.5 hidden sm:block" />
                  Overview
                </TabsTrigger>
              )}
              {isTabVisible('costing') && (
                <TabsTrigger value="costing" className="gap-1.5 text-xs sm:text-sm flex-1 min-w-[80px]" data-testid="tab-costing">
                  <Package className="w-3.5 h-3.5 hidden sm:block" />
                  Costing ({dashboard?.summary?.total_costing_errors || 0})
                </TabsTrigger>
              )}
              {isTabVisible('sales') && (
                <TabsTrigger value="sales" className="gap-1.5 text-xs sm:text-sm flex-1 min-w-[80px]" data-testid="tab-sales">
                  <TrendingUp className="w-3.5 h-3.5 hidden sm:block" />
                  Sales ({dashboard?.summary?.total_sales_errors || 0})
                </TabsTrigger>
              )}
              {isTabVisible('unassigned') && (
                <TabsTrigger value="unassigned" className="gap-1.5 text-xs sm:text-sm flex-1 min-w-[80px]" data-testid="tab-unassigned">
                  <Users className="w-3.5 h-3.5 hidden sm:block" />
                  Unassigned ({dashboard?.summary?.unassigned_orders || 0})
                </TabsTrigger>
              )}
              {isTabVisible('history') && (
                <TabsTrigger value="history" className="gap-1.5 text-xs sm:text-sm flex-1 min-w-[80px]" data-testid="tab-history">
                  <History className="w-3.5 h-3.5 hidden sm:block" />
                  History
                </TabsTrigger>
              )}
              <TabsTrigger value="missing_tracking" className="gap-1.5 text-xs sm:text-sm flex-1 min-w-[80px]" data-testid="tab-missing-tracking">
                <AlertTriangle className="w-3.5 h-3.5 hidden sm:block" />
                No Tracking ({dashboard?.summary?.missing_tracking || 0})
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          
          <CardContent className="pt-4">
            {/* Search and Filters */}
            {(activeTab === "costing" || activeTab === "sales" || activeTab === "unassigned") && (
              <>
                {/* Mobile: Collapsible Filter Panel */}
                <div className="lg:hidden mb-4">
                  <button
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 active:bg-slate-150 transition-colors"
                    data-testid="error-filter-toggle-btn"
                  >
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                      <span className="text-xs font-medium text-slate-700">Search & Filters</span>
                      {activeFilterCount > 0 && (
                        <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{totalErrors} errors</span>
                      {filtersExpanded ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {/* Mobile Expandable Filter Content */}
                  <div className={`overflow-hidden transition-all duration-200 ease-in-out ${filtersExpanded ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                    <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder="Search tracking, product, client..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          className="pl-10 h-9 text-xs"
                        />
                      </div>

                      {/* Date Range */}
                      <div className="grid grid-cols-2 gap-2">
                        <DMYDateInput
  placeholder="From"
                          value={dateFrom}
                          onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                          className="h-9 text-xs"
                        />
                        <DMYDateInput
  placeholder="To"
                          value={dateTo}
                          onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                          className="h-9 text-xs"
                        />
                      </div>

                      {/* Account & Error Type Filters */}
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={filterClient || "ALL"} onValueChange={(v) => setFilterClient(v === "ALL" ? "" : v)}>
                          <SelectTrigger className="h-9 text-xs">
                            <SelectValue placeholder="Account" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ALL">All Accounts</SelectItem>
                            <SelectItem value="UNKNOWN">Unknown</SelectItem>
                            {activeTab === "costing" && dashboard?.costing_by_client?.map(c => (
                              <SelectItem key={c.client_code} value={c.client_code}>
                                {c.client_code} ({c.error_count})
                              </SelectItem>
                            ))}
                            {activeTab === "sales" && (() => {
                              const clientList = salesErrorType === "missing_cost" 
                                ? dashboard?.missing_cost_by_client 
                                : salesErrorType === "negative_profit" 
                                  ? dashboard?.negative_profit_by_client 
                                  : dashboard?.sales_by_client;
                              return clientList?.map(c => (
                                <SelectItem key={c.client_code} value={c.client_code}>
                                  {c.client_code} ({c.error_count})
                                </SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>

                        {activeTab === "costing" && (
                          <Select value={filterErrorType || "ALL"} onValueChange={(v) => setFilterErrorType(v === "ALL" ? "" : v)}>
                            <SelectTrigger className="h-9 text-xs">
                              <SelectValue placeholder="Error Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ALL">All Errors</SelectItem>
                              <SelectItem value="Product not found">
                                All Product Errors ({dashboard?.summary?.total_costing_product_errors || 0})
                              </SelectItem>
                              {dashboard?.error_types?.map(e => (
                                <SelectItem key={e.type} value={e.type}>
                                  {e.type} ({e.count})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {activeTab === "sales" && (
                          <Select value={salesErrorType || "ALL"} onValueChange={(v) => { 
                            setSalesErrorType(v === "ALL" ? "" : v); 
                            setFilterClient("");
                            setCurrentPage(1); 
                          }}>
                            <SelectTrigger className="h-9 text-xs" data-testid="sales-error-type-filter-mobile">
                              <SelectValue placeholder="Error Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ALL">All Sales Errors</SelectItem>
                              <SelectItem value="missing_cost">Missing Cost</SelectItem>
                              <SelectItem value="negative_profit">Negative Profit</SelectItem>
                            </SelectContent>
                          </Select>
                        )}

                        {activeTab === "unassigned" && <div />}
                      </div>

                      {/* Clear Filters */}
                      {activeFilterCount > 0 && (
                        <button
                          onClick={() => {
                            setFilterClient("");
                            setFilterErrorType("");
                            setSalesErrorType("");
                            setSearchTerm("");
                            setDateFrom("");
                            setDateTo("");
                            setCurrentPage(1);
                          }}
                          className="w-full text-xs text-orange-600 hover:text-orange-700 font-medium py-1"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop: Always visible filters */}
                <div className="hidden lg:block space-y-3 mb-4">
                  {/* Search Bar */}
                  <div className="flex gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by tracking, product name, client, error message..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-10"
                      />
                    </div>
                    <div className="flex gap-2">
                      <DMYDateInput
  placeholder="From Date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                        className="w-40"
                      />
                      <DMYDateInput
  placeholder="To Date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                        className="w-40"
                      />
                    </div>
                  </div>
                  
                  {/* Dropdown Filters */}
                  <div className="flex gap-4">
                    <div className="w-48">
                      <Select value={filterClient || "ALL"} onValueChange={(v) => setFilterClient(v === "ALL" ? "" : v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Filter by Account" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALL">All Accounts</SelectItem>
                          <SelectItem value="UNKNOWN">Unknown / No Account</SelectItem>
                          {activeTab === "costing" && dashboard?.costing_by_client?.map(c => (
                            <SelectItem key={c.client_code} value={c.client_code}>
                              {c.client_code} ({c.error_count})
                            </SelectItem>
                          ))}
                          {activeTab === "sales" && (() => {
                            const clientList = salesErrorType === "missing_cost" 
                              ? dashboard?.missing_cost_by_client 
                              : salesErrorType === "negative_profit" 
                                ? dashboard?.negative_profit_by_client 
                                : dashboard?.sales_by_client;
                            return clientList?.map(c => (
                              <SelectItem key={c.client_code} value={c.client_code}>
                                {c.client_code} ({c.error_count})
                              </SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    {activeTab === "costing" && (
                      <div className="w-60">
                        <Select value={filterErrorType || "ALL"} onValueChange={(v) => setFilterErrorType(v === "ALL" ? "" : v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Filter by Error Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ALL">All Errors</SelectItem>
                            <SelectItem value="Product not found" data-testid="all-product-errors-option">
                              All Product Errors ({dashboard?.summary?.total_costing_product_errors || 0})
                            </SelectItem>
                            {dashboard?.error_types?.map(e => (
                              <SelectItem key={e.type} value={e.type}>
                                {e.type} ({e.count})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {activeTab === "sales" && (
                      <div className="w-60">
                        <Select value={salesErrorType || "ALL"} onValueChange={(v) => { 
                          setSalesErrorType(v === "ALL" ? "" : v); 
                          setFilterClient("");
                          setCurrentPage(1); 
                        }}>
                          <SelectTrigger data-testid="sales-error-type-filter">
                            <SelectValue placeholder="Filter by Error Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ALL">All Sales Errors ({dashboard?.summary?.total_sales_errors || 0})</SelectItem>
                            <SelectItem value="missing_cost">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                Missing Cost ({dashboard?.summary?.missing_cost_count || 0})
                              </span>
                            </SelectItem>
                            <SelectItem value="negative_profit">
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                Negative Profit ({dashboard?.summary?.negative_profit_count || 0})
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {(filterClient || searchTerm || dateFrom || dateTo || salesErrorType) && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => {
                          setFilterClient("");
                          setFilterErrorType("");
                          setSalesErrorType("");
                          setSearchTerm("");
                          setDateFrom("");
                          setDateTo("");
                          setCurrentPage(1);
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Recheck Errors Buttons */}
            {(activeTab === "costing" || activeTab === "sales" || activeTab === "unassigned" || activeTab === "overview") && (
              <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 text-sm text-blue-700 w-full sm:w-auto mb-1 sm:mb-0">
                  <RefreshCw className="w-4 h-4" />
                  <span className="font-medium">Recheck Errors:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                {(activeTab === "overview" || activeTab === "costing") && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleRecheckErrors('shipping')}
                    disabled={recheckingErrors}
                    className="gap-1.5 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm"
                  >
                    {recheckingErrors && recheckType === 'shipping' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Package className="w-3.5 h-3.5" />
                    )}
                    Shipping
                  </Button>
                )}
                {(activeTab === "overview" || activeTab === "costing") && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleRecheckErrors('weight')}
                    disabled={recheckingErrors}
                    className="gap-1.5 border-orange-200 hover:bg-orange-50 text-xs sm:text-sm"
                    data-testid="recheck-weight-errors"
                  >
                    {recheckingErrors && recheckType === 'weight' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Weight className="w-3.5 h-3.5" />
                    )}
                    Weight
                  </Button>
                )}
                {(activeTab === "overview" || activeTab === "costing" || activeTab === "sales") && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleRecheckErrors('products')}
                    disabled={recheckingErrors}
                    className="gap-1.5 border-amber-200 hover:bg-amber-50 text-xs sm:text-sm"
                  >
                    {recheckingErrors && recheckType === 'products' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <DollarSign className="w-3.5 h-3.5" />
                    )}
                    Products
                  </Button>
                )}
                {(activeTab === "overview" || activeTab === "unassigned") && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleRecheckErrors('unassigned')}
                    disabled={recheckingErrors}
                    className="gap-1.5 border-purple-200 hover:bg-purple-50 text-xs sm:text-sm"
                  >
                    {recheckingErrors && recheckType === 'unassigned' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Users className="w-3.5 h-3.5" />
                    )}
                    Unassigned
                  </Button>
                )}
                {activeTab === "overview" && (
                  <Button 
                    size="sm" 
                    onClick={() => handleRecheckErrors('all')}
                    disabled={recheckingErrors}
                    className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                  >
                    {recheckingErrors && recheckType === 'all' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    Recheck All
                  </Button>
                )}
                </div>
                <span className="text-[10px] sm:text-xs text-slate-500 w-full sm:w-auto sm:ml-auto mt-1 sm:mt-0">
                  Re-validates errors against current data
                </span>
              </div>
            )}

            {/* Bulk Actions */}
            {activeTab === "unassigned" && selectedItems.length > 0 && (
              <div className="flex items-center gap-4 mb-4 p-3 bg-blue-50 rounded-lg">
                <span className="text-sm font-medium text-blue-700">
                  {selectedItems.length} items selected
                </span>
                <Button size="sm" onClick={() => setBulkAssignDialog(true)} className="gap-2">
                  <UserPlus className="w-4 h-4" />
                  Bulk Assign Client
                </Button>
                <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="gap-2">
                  <Trash2 className="w-4 h-4" />
                  Delete Selected
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedItems([])}>
                  Clear Selection
                </Button>
              </div>
            )}

            {/* Costing Errors Tab */}
            <TabsContent value="costing" className="mt-0">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : costingErrors.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-600">No costing errors!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Export/Print Toolbar for Costing */}
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <FileSpreadsheet className="w-4 h-4" />
                      <span>{costingErrors.length} of {totalErrors} records</span>
                      {(filterClient || filterErrorType || dateFrom || dateTo) && (
                        <Badge variant="outline" className="text-xs">Filtered</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Enhanced Excel Export Button */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-2" disabled={exporting} data-testid="costing-excel-export-btn">
                            <Download className="w-4 h-4" />
                            Export Excel
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64">
                          <DropdownMenuLabel className="flex items-center justify-between">
                            <span>Quick Export (Current Page)</span>
                            <span
                              className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"
                              data-testid="export-dropdown-total-pill"
                            >
                              Total: {totalErrors}
                            </span>
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleExportCostingExcel('all')} data-testid="export-all-costing">
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Current Page ({costingErrors.length})
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-slate-500">By Error Type</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleExportCostingExcel('product')}>
                            <Package className="w-4 h-4 mr-2 text-red-500" />
                            Product Issues Only ({costingExportCounts.product})
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleExportProductTemplate} data-testid="export-product-template" className="bg-indigo-50/60 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-300">
                            <Package className="w-4 h-4 mr-2 text-indigo-600" />
                            <div className="flex flex-col">
                              <span className="font-medium">Product Issues → Price List Template ({costingExportCounts.product})</span>
                              <span className="text-[10px] text-indigo-600/70">Ready for direct upload to Product Prices</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportCostingExcel('shipping')}>
                            <TrendingUp className="w-4 h-4 mr-2 text-orange-500" />
                            Shipping Issues Only ({costingExportCounts.shipping})
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleExportShippingTemplate} data-testid="export-shipping-template" className="bg-orange-50/60 text-orange-700 hover:bg-orange-100 dark:bg-orange-950/40 dark:text-orange-300">
                            <TrendingUp className="w-4 h-4 mr-2 text-orange-600" />
                            <div className="flex flex-col">
                              <span className="font-medium">Shipping Issues → Rates Template ({costingExportCounts.shipping})</span>
                              <span className="text-[10px] text-orange-600/70">Ready for direct upload to Shipping Rates</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-green-600 font-semibold">Full Export Options</DropdownMenuLabel>
                          <DropdownMenuItem 
                            onClick={() => { setExportDialogMode("excel"); setExportDialogOpen(true); }}
                            className="bg-green-50 text-green-700 hover:bg-green-100"
                          >
                            <CalendarRange className="w-4 h-4 mr-2" />
                            Export All / By Date...
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      {/* Enhanced Print Button */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-2" data-testid="costing-print-btn">
                            <Printer className="w-4 h-4" />
                            Print
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Quick Print (Current Page)</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handlePrintCosting('all')}>
                            <Printer className="w-4 h-4 mr-2" />
                            Print Current Page
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePrintCosting('product')}>
                            <Package className="w-4 h-4 mr-2" />
                            Product Issues Only ({costingExportCounts.product})
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-blue-600 font-semibold">Full Print Options</DropdownMenuLabel>
                          <DropdownMenuItem 
                            onClick={() => { setExportDialogMode("print"); setExportDialogOpen(true); }}
                            className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            <CalendarRange className="w-4 h-4 mr-2" />
                            Print All / By Date...
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <SortableHeader column="tracking" label="Tracking" sortConfig={sortConfig} onSort={handleSort} />
                        <SortableHeader column="client_code" label="Client" sortConfig={sortConfig} onSort={handleSort} />
                        <SortableHeader column="product" label="Product" sortConfig={sortConfig} onSort={handleSort} />
                        <SortableHeader column="errors" label="Error" sortConfig={sortConfig} onSort={handleSort} />
                        <SortableHeader column="order_date" label="Date" sortConfig={sortConfig} onSort={handleSort} />
                        <th className="p-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortData(costingErrors).map((error, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-2 font-mono text-xs">{error.tracking}</td>
                          <td className="p-2">{error.client_code || <span className="text-slate-400">-</span>}</td>
                          <td className="p-2 max-w-[200px]">
                            {/* Show ONLY products that have errors, not first product */}
                            {(() => {
                              // Use error_products if available (new field from backend), otherwise filter products
                              const errorProducts = error.error_products || 
                                (error.products ? error.products.filter(p => p.errors && p.errors.length > 0) : []);
                              
                              if (errorProducts.length > 0) {
                                return (
                                  <div className="space-y-1">
                                    {errorProducts.slice(0, 3).map((p, pIdx) => (
                                      <div key={pIdx} className="truncate text-sm" title={p.second_name || p.product}>
                                        <span className="text-red-600">•</span> {(p.second_name || p.product || "-").slice(0, 40)}
                                        {(p.second_name || p.product || "").length > 40 && "..."}
                                      </div>
                                    ))}
                                    {errorProducts.length > 3 && (
                                      <span className="text-xs text-blue-600 font-medium">
                                        +{errorProducts.length - 3} more error product{errorProducts.length > 4 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="truncate" title={error.second_name || error.product}>
                                    {error.second_name || error.product || "-"}
                                  </div>
                                );
                              }
                            })()}
                          </td>
                          <td className="p-2">
                            {renderErrors(error.errors, error)}
                          </td>
                          <td className="p-2 text-xs">{formatDMY(error.order_date)}</td>
                          <td className="p-2">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openResolveDialog(error, "edit_product")}
                                title="Edit Product"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {error.errors?.some(e => e.toLowerCase().includes("country")) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => openResolveDialog(error, "assign_country")}
                                  title="Assign Country"
                                  className="text-blue-600"
                                >
                                  <Globe className="w-4 h-4" />
                                </Button>
                              )}
                              {error.errors?.some(e => e.toLowerCase().includes("service") || e.toLowerCase().includes("shipping")) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => openResolveDialog(error, "assign_service")}
                                  title="Fix Shipping Service"
                                  className="text-orange-600"
                                  data-testid="fix-shipping-btn"
                                >
                                  <Package className="w-4 h-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openResolveDialog(error, "manual_price")}
                                title="Manual Price"
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openResolveDialog(error, "recalculate")}
                                title="Recalculate"
                              >
                                <Calculator className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openEditRecordDialog(error)}
                                title="Edit Record (Weight/Qty/etc)"
                                className="text-purple-600"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openResolveDialog(error, "mark_ignored")}
                                title="Mark Ignored"
                              >
                                <XCircle className="w-4 h-4 text-orange-500" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openDeleteDialog(error)}
                                title="Delete Record"
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </TabsContent>

            {/* Sales Errors Tab */}
            <TabsContent value="sales" className="mt-0">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : salesErrors.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-600">No unmatched sales!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Role-based quick filter badges */}
                  <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 rounded-lg border">
                    <span className="text-xs text-slate-500 font-medium">Quick Filter:</span>
                    <Badge 
                      className={`cursor-pointer text-xs ${!filterClient ? 'bg-slate-800 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                      onClick={() => { setFilterClient(""); setCurrentPage(1); }}
                      data-testid="filter-all-sales"
                    >
                      All ({dashboard?.summary?.total_sales_errors?.toLocaleString() || 0})
                    </Badge>
                    <span className="text-slate-300">|</span>
                    <span className="text-[10px] text-indigo-600 font-semibold">Partners/Agents:</span>
                    {(dashboard?.sales_by_partner || []).slice(0, 6).map((p, i) => (
                      <Badge
                        key={`p-${i}`}
                        className={`cursor-pointer text-[10px] ${filterClient === p.client_code ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'}`}
                        onClick={() => { setFilterClient(p.client_code); setCurrentPage(1); }}
                      >
                        {p.client_code} ({p.error_count.toLocaleString()})
                      </Badge>
                    ))}
                    {(dashboard?.sales_by_partner?.length || 0) > 6 && (
                      <span className="text-[10px] text-slate-400">+{dashboard.sales_by_partner.length - 6} more</span>
                    )}
                    <span className="text-slate-300">|</span>
                    <span className="text-[10px] text-teal-600 font-semibold">DP Clients:</span>
                    {(dashboard?.sales_by_dp_client || []).slice(0, 6).map((p, i) => (
                      <Badge
                        key={`d-${i}`}
                        className={`cursor-pointer text-[10px] ${filterClient === p.client_code ? 'bg-teal-600 text-white' : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'}`}
                        onClick={() => { setFilterClient(p.client_code); setCurrentPage(1); }}
                      >
                        {p.client_code} ({p.error_count.toLocaleString()})
                      </Badge>
                    ))}
                    {(dashboard?.sales_by_dp_client?.length || 0) > 6 && (
                      <span className="text-[10px] text-slate-400">+{dashboard.sales_by_dp_client.length - 6} more</span>
                    )}
                  </div>
                  
                  {/* Export/Print Toolbar for Sales */}
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <TrendingUp className="w-4 h-4" />
                      <span>{salesErrors.length} sales errors</span>
                      {filterClient && (
                        <Badge variant="outline" className="text-xs">Client: {filterClient}</Badge>
                      )}
                      {salesErrorType && (
                        <Badge variant="outline" className={`text-xs ${salesErrorType === 'negative_profit' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                          {salesErrorType === 'negative_profit' ? 'Negative Profit' : 'Missing Cost'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Sync Costs from Shipment Costing Button */}
                      {isAdmin && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50" 
                          disabled={syncingCosts}
                          onClick={async () => {
                            setSyncingCosts(true);
                            try {
                              toast.info("Syncing costs from Shipment Costing...");
                              const response = await fetch(`${BACKEND_URL}/api/sales/sync-costs-from-orders`, {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({ 
                                  client_code: filterClient || undefined,
                                  force_all: false 
                                })
                              });
                              const data = await response.json();
                              if (response.ok) {
                                toast.success(`Synced costs for ${data.updated_count} of ${data.total_checked} records`);
                                fetchSalesErrors();
                                fetchDashboard();
                              } else {
                                toast.error(data.detail || "Sync failed");
                              }
                            } catch (error) {
                              console.error("Sync error:", error);
                              toast.error("Sync failed");
                            } finally {
                              setSyncingCosts(false);
                            }
                          }}
                          data-testid="sync-costs-btn"
                          title="Update sales costs from latest Shipment Costing data"
                        >
                          <RefreshCw className={`w-4 h-4 ${syncingCosts ? 'animate-spin' : ''}`} />
                          {syncingCosts ? "Syncing..." : "Sync Costs from Costing"}
                        </Button>
                      )}
                      {isAdmin && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2 border-teal-300 text-teal-700 hover:bg-teal-50" 
                          disabled={regeneratingDP}
                          onClick={async () => {
                            // Determine which DP clients to regenerate
                            const dpClientCodes = (dashboard?.sales_by_dp_client || []).map(c => c.client_code);
                            const targetClients = filterClient && dpClientCodes.includes(filterClient) 
                              ? [filterClient] 
                              : dpClientCodes;
                            
                            if (targetClients.length === 0) {
                              toast.info("No DP Client sales errors to auto-calculate");
                              return;
                            }
                            
                            const clientLabel = targetClients.length === 1 ? targetClients[0] : `${targetClients.length} DP Clients`;
                            setRegeneratingDP(true);
                            try {
                              toast.info(`Auto-calculating sales for ${clientLabel}...`);
                              const response = await fetch(`${BACKEND_URL}/api/sales/regenerate-dp-client`, {
                                method: "POST",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({ client_codes: targetClients })
                              });
                              const data = await response.json();
                              if (response.ok) {
                                toast.success(`Generated ${data.generated || 0} sales records for ${clientLabel}. Deleted ${data.deleted_old_records || 0} old placeholders.`);
                                fetchSalesErrors();
                                fetchDashboard();
                              } else {
                                toast.error(data.detail || "Auto-calculate failed");
                              }
                            } catch (error) {
                              console.error("Auto-calculate error:", error);
                              toast.error("Auto-calculate failed");
                            } finally {
                              setRegeneratingDP(false);
                            }
                          }}
                          data-testid="auto-calculate-dp-sales-btn"
                          title="Auto-calculate sell prices for DP Client orders using their price list"
                        >
                          <Calculator className={`w-4 h-4 ${regeneratingDP ? 'animate-spin' : ''}`} />
                          {regeneratingDP ? "Calculating..." : (filterClient ? "Auto-Calculate Sales" : "Auto-Calculate DP Sales")}
                        </Button>
                      )}
                      {isAdmin && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2" 
                          onClick={handleExportSalesExcel}
                          disabled={exporting}
                          data-testid="sales-excel-export-btn"
                        >
                          <Download className="w-4 h-4" />
                          Export Excel
                        </Button>
                      )}
                      {isAdmin && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-2" 
                          onClick={handlePrintSales}
                          data-testid="sales-print-btn"
                        >
                          <Printer className="w-4 h-4" />
                          Print
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th 
                          className="p-2 text-left cursor-pointer hover:bg-slate-200 select-none"
                          onClick={() => handleSalesSort("tracking")}
                        >
                          <div className="flex items-center">
                            Tracking
                            {salesSortField === "tracking" ? (
                              salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                            ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                          </div>
                        </th>
                        <th 
                          className="p-2 text-left cursor-pointer hover:bg-slate-200 select-none"
                          onClick={() => handleSalesSort("client_code")}
                        >
                          <div className="flex items-center">
                            Client
                            {salesSortField === "client_code" ? (
                              salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                            ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                          </div>
                        </th>
                        <th 
                          className="p-2 text-left cursor-pointer hover:bg-slate-200 select-none"
                          onClick={() => handleSalesSort("customer_name")}
                        >
                          <div className="flex items-center">
                            Name
                            {salesSortField === "customer_name" ? (
                              salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                            ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                          </div>
                        </th>
                        {isAdmin && (
                          <th 
                            className="p-2 text-right cursor-pointer hover:bg-slate-200 select-none"
                            onClick={() => handleSalesSort("sell_price")}
                          >
                            <div className="flex items-center justify-end">
                              Sell Price
                              {salesSortField === "sell_price" ? (
                                salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                              ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                            </div>
                          </th>
                        )}
                        <th 
                          className="p-2 text-right cursor-pointer hover:bg-slate-200 select-none"
                          onClick={() => handleSalesSort("cost_price")}
                        >
                          <div className="flex items-center justify-end">
                            Cost
                            {salesSortField === "cost_price" ? (
                              salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                            ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                          </div>
                        </th>
                        {isAdmin && (
                          <th 
                            className="p-2 text-right cursor-pointer hover:bg-slate-200 select-none"
                            onClick={() => handleSalesSort("profit")}
                          >
                            <div className="flex items-center justify-end">
                              Profit
                              {salesSortField === "profit" ? (
                                salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                              ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                            </div>
                          </th>
                        )}
                        <th className="p-2 text-left font-medium text-slate-600">Error Type</th>
                        <th 
                          className="p-2 text-left cursor-pointer hover:bg-slate-200 select-none"
                          onClick={() => handleSalesSort("sale_date")}
                        >
                          <div className="flex items-center">
                            Date
                            {salesSortField === "sale_date" ? (
                              salesSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />
                            ) : <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />}
                          </div>
                        </th>
                        <th className="p-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesErrors.map((sale, idx) => {
                        const isNegativeProfit = sale.error_type === "negative_profit" || (sale.profit && sale.profit < 0);
                        const isMissingCost = sale.error_type === "missing_cost" || (!sale.cost_price && sale.cost_price !== 0);
                        const rowKey = sale.id || sale.tracking || idx;
                        const isExpanded = expandedRows.has(rowKey);
                        
                        // Get client code first (needed for other checks)
                        const clientCode = sale.client_code;
                        
                        // Check if this is a DP Client (client_role = "client")
                        // For missing_cost errors, ANY client needs to add products to their price list
                        const isDpClient = sale.client_role?.toLowerCase() === "client";
                        // Show "Add to Ledger" for any client with missing cost (they all have price lists)
                        const canAddToLedger = !!clientCode && isMissingCost;
                        
                        // Get products - try multiple sources
                        let products = sale.products?.filter(p => p.name && p.name !== 'nan' && p.name.trim()) || [];
                        
                        // If no products array, create one from product_name or second_name
                        if (products.length === 0) {
                          const productName = sale.product_name || sale.second_name || sale.product;
                          if (productName && productName !== 'nan' && productName.trim()) {
                            products = [{ name: productName, quantity: sale.quantity || 1 }];
                          }
                        }
                        
                        const toggleRow = () => {
                          setExpandedRows(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(rowKey)) {
                              newSet.delete(rowKey);
                            } else {
                              newSet.add(rowKey);
                            }
                            return newSet;
                          });
                        };
                        
                        return (
                          <React.Fragment key={rowKey}>
                            <tr 
                              className={`border-b hover:bg-slate-50 cursor-pointer ${isNegativeProfit ? 'bg-red-50' : ''} ${isExpanded ? 'bg-blue-50' : ''}`}
                              onClick={toggleRow}
                            >
                              <td className="p-2 font-mono text-xs">
                                <div className="flex items-center gap-2">
                                  <button className="p-0.5 hover:bg-slate-200 rounded">
                                    {isExpanded ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                  </button>
                                  <span>{sale.tracking || sale.tracking_number || "-"}</span>
                                </div>
                              </td>
                              <td className="p-2">{sale.client_code || "-"}</td>
                              <td className="p-2 max-w-[200px] truncate">{sale.customer_name || sale.name || "-"}</td>
                              {isAdmin && (
                                <td className="p-2 text-right">₹{(sale.sell_price || 0).toLocaleString()}</td>
                              )}
                              <td className="p-2 text-right text-red-500">
                                {sale.cost_price ? `₹${sale.cost_price.toLocaleString()}` : "Missing"}
                              </td>
                              {isAdmin && (
                                <td className={`p-2 text-right font-medium ${sale.profit < 0 ? 'text-red-600' : sale.profit > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                                  {sale.profit != null ? `₹${sale.profit.toLocaleString()}` : "-"}
                                </td>
                              )}
                              <td className="p-2">
                                {isNegativeProfit ? (
                                  <Badge className="bg-red-100 text-red-700 text-xs">Negative Profit</Badge>
                                ) : isMissingCost ? (
                                  <Badge className="bg-amber-100 text-amber-700 text-xs">Missing Cost</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-600 text-xs">{sale.error_type || "Error"}</Badge>
                                )}
                              </td>
                              <td className="p-2 text-xs">{formatDMY(sale.sale_date)}</td>
                              <td className="p-2" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-1">
                                  {/* Link to Order - for missing cost */}
                                  {isMissingCost && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={() => openLinkOrderDialog(sale)}
                                      title="Link to Order"
                                      className="text-blue-600"
                                    data-testid="link-order-btn"
                                  >
                                    <Link2 className="w-4 h-4" />
                                  </Button>
                                )}
                                
                                {/* Edit Sell Price - for negative profit (Admin only) */}
                                {isAdmin && isNegativeProfit && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => {
                                      setSelectedError(sale);
                                      setNewSellPrice(sale.sell_price?.toString() || "");
                                      setEditSellPriceDialog(true);
                                    }}
                                    title="Edit Sell Price"
                                    className="text-green-600"
                                    data-testid="edit-sell-price-btn"
                                  >
                                    <DollarSign className="w-4 h-4" />
                                  </Button>
                                )}
                                
                                {/* Manual Cost Entry */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => openManualCostDialog(sale)}
                                  title="Manual Cost Entry"
                                  className="text-emerald-600"
                                  data-testid="manual-cost-btn"
                                >
                                  <CircleDollarSign className="w-4 h-4" />
                                </Button>
                                
                                {/* Edit Quantity */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => {
                                    setSelectedError(sale);
                                    setNewQuantity(sale.quantity?.toString() || "1");
                                    setEditQuantityDialog(true);
                                  }}
                                  title="Edit Quantity"
                                  className="text-amber-600"
                                  data-testid="edit-quantity-btn"
                                >
                                  <Hash className="w-4 h-4" />
                                </Button>
                                
                                {/* Link to another Sale */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => {
                                    setSelectedError(sale);
                                    setLinkSaleDialog(true);
                                  }}
                                  title="Link to Another Sale"
                                  className="text-purple-600"
                                  data-testid="link-sale-btn"
                                >
                                  <Shuffle className="w-4 h-4" />
                                </Button>
                                
                                {/* Assign Client */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => openResolveDialog(sale, "assign_client")}
                                  title="Assign Client"
                                  data-testid="assign-client-btn"
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                                
                                {/* Edit Date */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => {
                                    setSelectedError(sale);
                                    setNewSaleDate(sale.sale_date || "");
                                    setResolutionNotes("");
                                    setEditSaleDateDialog(true);
                                  }}
                                  title="Edit Date"
                                  className="text-blue-600"
                                  data-testid="edit-date-btn"
                                >
                                  <Calendar className="w-4 h-4" />
                                </Button>
                                
                                {/* Narration Logic - for all users */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => {
                                    setNarrationSale(sale);
                                    setNarrationText("");
                                    setNarrationLogicDialog(true);
                                  }}
                                  title="Add Narration Logic"
                                  className="text-purple-600"
                                  data-testid="narration-logic-btn"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                
                                {/* Narration Manual - for all users */}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => {
                                    setNarrationSale(sale);
                                    setNarrationText("");
                                    setNarrationManualDialog(true);
                                  }}
                                  title="Add Manual Narration"
                                  className="text-indigo-600"
                                  data-testid="narration-manual-btn"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                
                                {/* Ignore - Admin only */}
                                {isAdmin && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => {
                                      setSelectedError(sale);
                                      setIgnoreDialog(true);
                                    }}
                                    title="Ignore Error"
                                    className="text-amber-600"
                                    data-testid="ignore-error-btn"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Expanded Products Row - Enhanced for Sales with DP Client support */}
                          {isExpanded && (
                            <tr className="bg-gradient-to-r from-blue-50 to-slate-50">
                              <td colSpan={isAdmin ? 9 : 7} className="p-0">
                                <div className="px-8 py-4 border-l-4 border-blue-400">
                                  {/* Info banner for clients with missing cost */}
                                  {canAddToLedger && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                                      <Info className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-green-800">Client: {clientCode}</p>
                                        <p className="text-xs text-green-700 mt-0.5">
                                          This product is not in {clientCode}'s price list. Use "Add to Ledger" to add the product with a sell price.
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="flex items-start gap-6">
                                    <div className="flex-1">
                                      <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2 flex items-center gap-2">
                                        <Package className="w-3.5 h-3.5" />
                                        Products in this Order ({products.length})
                                        {canAddToLedger && (
                                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                                            Price List Update Needed
                                          </Badge>
                                        )}
                                      </h4>
                                      
                                      {products.length > 0 ? (
                                        <div className="space-y-2">
                                          {products.map((prod, pIdx) => (
                                            <div 
                                              key={pIdx} 
                                              className={`bg-white rounded-lg px-4 py-3 border ${isMissingCost ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`}
                                            >
                                              <div className="flex items-center justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-slate-800">{prod.name}</span>
                                                    {prod.quantity > 0 && (
                                                      <Badge variant="secondary" className="text-[10px]">× {prod.quantity}</Badge>
                                                    )}
                                                  </div>
                                                  
                                                  {/* Show error indicator for missing cost */}
                                                  {isMissingCost && (
                                                    <div className="flex items-center gap-2 mt-1.5">
                                                      <Badge variant="destructive" className="text-[10px]">Missing Sell Price</Badge>
                                                      <span className="text-[10px] text-slate-500">
                                                        Not found in {clientCode}'s price list
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                                
                                                {/* Actions Column */}
                                                <div className="flex items-center gap-2 shrink-0">
                                                  {/* SalesProductFix button for any client with missing cost */}
                                                  {canAddToLedger && (
                                                    <SalesProductFix
                                                      clientCode={clientCode}
                                                      productName={prod.name}
                                                      token={token}
                                                      onFixed={() => {
                                                        fetchDashboard();
                                                        fetchSalesErrors?.();
                                                      }}
                                                    />
                                                  )}
                                                  <Package className="w-4 h-4 text-slate-300" />
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <p className="text-sm text-slate-400 italic">No product details available</p>
                                      )}
                                    </div>
                                    
                                    {/* Quick Actions - Admin only */}
                                    {isAdmin && (
                                      <div className="flex flex-col gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`/costing?search=${sale.tracking || sale.tracking_number}`, '_blank');
                                          }}
                                          className="text-xs gap-1"
                                        >
                                          <Search className="w-3 h-3" />
                                          View in Costing
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </TabsContent>

            {/* Unassigned Orders Tab */}
            <TabsContent value="unassigned" className="mt-0">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : unassignedOrders.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-600">All orders are assigned!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Enhanced Info Banner for Incomplete Records */}
                  {unassignedOrders.filter(o => o.is_incomplete).length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800 text-sm">
                          {unassignedOrders.filter(o => o.is_incomplete).length} records have incomplete data
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          These records are missing critical information like tracking numbers or country. 
                          Click on a row to see details and determine if they should be deleted or completed.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Export/Print Toolbar for Unassigned */}
                  <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border">
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <Users className="w-4 h-4" />
                      <span>{unassignedOrders.length} unassigned orders</span>
                      {unassignedOrders.filter(o => o.is_incomplete).length > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          {unassignedOrders.filter(o => o.is_incomplete).length} severely incomplete
                        </Badge>
                      )}
                      {unassignedOrders.filter(o => !o.has_tracking).length > 0 && (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                          {unassignedOrders.filter(o => !o.has_tracking).length} no tracking
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2" 
                        onClick={handleExportUnassignedExcel}
                        disabled={exporting}
                        data-testid="unassigned-excel-export-btn"
                      >
                        <Download className="w-4 h-4" />
                        Export Excel
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="gap-2" 
                        onClick={handlePrintUnassigned}
                        data-testid="unassigned-print-btn"
                      >
                        <Printer className="w-4 h-4" />
                        Print
                      </Button>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                  <div className="mb-2 flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={selectAll}>
                      Select All ({unassignedOrders.length})
                    </Button>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-2 w-8">
                          <input 
                            type="checkbox" 
                            checked={selectedItems.length === unassignedOrders.length && unassignedOrders.length > 0}
                            onChange={(e) => e.target.checked ? selectAll() : setSelectedItems([])}
                          />
                        </th>
                        <SortableHeader column="tracking" label="Tracking" sortConfig={sortConfig} onSort={handleSort} />
                        <th className="p-2 text-left text-xs font-medium text-slate-600">Products</th>
                        <SortableHeader column="detected_country" label="Country" sortConfig={sortConfig} onSort={handleSort} />
                        <SortableHeader column="total_cost" label="Total Cost" className="text-right" sortConfig={sortConfig} onSort={handleSort} />
                        <th className="p-2 text-left text-xs font-medium text-slate-600">Upload Source</th>
                        <SortableHeader column="order_date" label="Date" sortConfig={sortConfig} onSort={handleSort} />
                        <th className="p-2 text-center text-xs font-medium text-slate-600">Status</th>
                        <th className="p-2 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortData(unassignedOrders).map((order, idx) => {
                        // Use enhanced fields from backend if available
                        const isIncomplete = order.is_incomplete ?? (!order.tracking || !order.detected_country || !order.order_date);
                        const missingFields = order.missing_fields || [];
                        const products = order.products || [];
                        const productCount = products.length || order.parcel_product_count || 1;
                        const isExpanded = expandedUnassignedRows?.includes(order.record_id || order.tracking || idx);
                        const rowId = order.record_id || order.tracking || `row-${idx}`;
                        
                        return (
                          <React.Fragment key={rowId}>
                            <tr 
                              className={`border-b hover:bg-slate-50 cursor-pointer transition-colors ${
                                selectedItems.includes(order.tracking) ? 'bg-blue-50' : ''
                              } ${isIncomplete ? 'bg-red-50 border-l-4 border-l-red-400' : ''} ${isExpanded ? 'bg-blue-50/50' : ''}`}
                              onClick={() => {
                                setExpandedUnassignedRows(prev => 
                                  prev?.includes(rowId) 
                                    ? prev.filter(r => r !== rowId)
                                    : [...(prev || []), rowId]
                                );
                              }}
                              data-testid={`unassigned-row-${idx}`}
                            >
                              <td className="p-2" onClick={(e) => e.stopPropagation()}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedItems.includes(order.tracking)}
                                  onChange={() => toggleSelection(order.tracking)}
                                  disabled={!order.tracking}
                                />
                              </td>
                              <td className="p-2 font-mono text-xs">
                                <div className="flex items-center gap-1.5">
                                  <button className="p-0.5 hover:bg-slate-200 rounded">
                                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-blue-600" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                  </button>
                                  {order.tracking ? (
                                    <span className="font-medium">{order.tracking}</span>
                                  ) : (
                                    <span className="text-red-500 italic flex items-center gap-1">
                                      <XCircle className="w-3 h-3" />
                                      No tracking
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 max-w-[200px]">
                                <div className="text-xs">
                                  {products.length > 0 ? (
                                    <div className="flex items-center gap-1">
                                      <span className="truncate max-w-[150px]" title={products[0]?.name}>
                                        {products[0]?.name || '-'}
                                      </span>
                                      {productCount > 1 && (
                                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                                          +{productCount - 1} more
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-red-500 italic">No product</span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 text-xs">
                                <div className="flex items-center gap-1">
                                  {order.detected_country || order.country ? (
                                    <>
                                      <Globe className="w-3 h-3 text-slate-400" />
                                      {order.detected_country || order.country}
                                      {order.detected_service && (
                                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded">{order.detected_service}</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-red-500 italic flex items-center gap-1">
                                      <XCircle className="w-3 h-3" />
                                      Unknown
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 text-right font-medium text-xs">
                                {(order.total_cost || order.display_total_cost) > 0 
                                  ? `₹${(order.total_cost || order.display_total_cost || 0).toLocaleString()}`
                                  : <span className="text-slate-400">₹0</span>
                                }
                              </td>
                              <td className="p-2 text-xs">
                                <div className="truncate max-w-[120px]" title={order.sheet_name}>
                                  {order.sheet_name || <span className="text-slate-400">Unknown source</span>}
                                </div>
                              </td>
                              <td className="p-2 text-xs">
                                {order.order_date ? (
                                  formatDMY(order.order_date)
                                ) : (
                                  <span className="text-red-500 italic">No date</span>
                                )}
                              </td>
                              <td className="p-2 text-center">
                                {isIncomplete ? (
                                  <Badge variant="destructive" className="text-[10px]">
                                    Missing: {missingFields.slice(0, 2).join(', ')}{missingFields.length > 2 ? '...' : ''}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">
                                    No Client
                                  </Badge>
                                )}
                              </td>
                              <td className="p-2" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => openResolveDialog(order, "assign_client")}
                                    title="Assign Client"
                                    data-testid={`assign-client-btn-${idx}`}
                                  >
                                    <UserPlus className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openDeleteDialog(order)}
                                    title="Delete Record"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-btn-${idx}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Expanded Details Row - Enhanced */}
                            {isExpanded && (
                              <tr className="bg-gradient-to-r from-slate-50 to-blue-50/30 border-b-2 border-blue-100">
                                <td colSpan={10} className="p-0">
                                  <div className="p-4 space-y-4">
                                    {/* Alert for incomplete records */}
                                    {isIncomplete && (
                                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                          <p className="text-sm font-medium text-red-800">This record is incomplete</p>
                                          <p className="text-xs text-red-600 mt-1">
                                            Missing: {missingFields.join(', ')}. 
                                            Consider deleting this orphan record if it cannot be completed.
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
                                      {/* Products Section - Enhanced */}
                                      <div className="md:col-span-2 bg-white rounded-lg border p-3">
                                        <p className="text-slate-600 font-semibold mb-2 flex items-center gap-2">
                                          <Package className="w-4 h-4" />
                                          Products ({products.length || 1})
                                        </p>
                                        {products.length > 0 ? (
                                          <div className="space-y-2 max-h-40 overflow-y-auto">
                                            {products.map((p, pi) => (
                                              <div key={pi} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                                <div className="flex-1">
                                                  <p className="font-medium text-slate-800">{p.name}</p>
                                                  <p className="text-slate-500">Qty: {p.qty || '-'}</p>
                                                </div>
                                                <div className="text-right">
                                                  <p className="font-medium">₹{(p.cost || 0).toLocaleString()}</p>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="text-center py-4 bg-slate-50 rounded">
                                            <p className="text-slate-400 italic">No product information available</p>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Cost & Shipping Section */}
                                      <div className="bg-white rounded-lg border p-3">
                                        <p className="text-slate-600 font-semibold mb-2 flex items-center gap-2">
                                          <DollarSign className="w-4 h-4" />
                                          Cost Breakdown
                                        </p>
                                        <div className="space-y-1">
                                          <div className="flex justify-between">
                                            <span className="text-slate-500">Medicine:</span>
                                            <span>₹{(order.medicine_cost || order.parcel_medicine_cost || 0).toLocaleString()}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-slate-500">Shipping:</span>
                                            <span>₹{(order.shipping_cost || order.parcel_shipping_cost || 0).toLocaleString()}</span>
                                          </div>
                                          <div className="flex justify-between font-semibold border-t pt-1 mt-1">
                                            <span>Total:</span>
                                            <span className="text-green-600">₹{(order.total_cost || order.parcel_total || 0).toLocaleString()}</span>
                                          </div>
                                          <div className="flex justify-between text-slate-500 text-[10px] mt-2 pt-2 border-t">
                                            <span>Weight:</span>
                                            <span>{order.gross_weight || order.parcel_gross_weight || '-'}g</span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Upload & Source Info Section */}
                                      <div className="bg-white rounded-lg border p-3">
                                        <p className="text-slate-600 font-semibold mb-2 flex items-center gap-2">
                                          <FileSpreadsheet className="w-4 h-4" />
                                          Source Information
                                        </p>
                                        <div className="space-y-2 text-xs">
                                          <div>
                                            <p className="text-slate-500">Uploaded From:</p>
                                            <p className="font-medium text-slate-800 truncate" title={order.sheet_name}>
                                              {order.sheet_name || 'Unknown'}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-slate-500">Upload Date:</p>
                                            <p className="font-medium">
                                              {order.uploaded_at 
                                                ? new Date(order.uploaded_at).toLocaleString()
                                                : '-'}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-slate-500">Row / Batch:</p>
                                            <p className="font-mono text-[10px]">
                                              Row #{order.row_number || '-'} • {order.upload_batch_id?.slice(0, 8) || '-'}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-slate-500">Address/Customer:</p>
                                            <p className="truncate font-medium" title={order.address || order.customer_name}>
                                              {order.address || order.customer_name || '-'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Action Bar */}
                                    <div className="flex items-center justify-between pt-2 border-t">
                                      <div className="text-[10px] text-slate-500">
                                        Record ID: {order.record_id || '-'}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={(e) => { e.stopPropagation(); openResolveDialog(order, "assign_client"); }}
                                          className="gap-1"
                                        >
                                          <UserPlus className="w-3 h-3" />
                                          Assign Client
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          onClick={(e) => { e.stopPropagation(); openEditRecordDialog(order); }}
                                          className="gap-1"
                                        >
                                          <Pencil className="w-3 h-3" />
                                          Edit
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="destructive"
                                          onClick={(e) => { e.stopPropagation(); openDeleteDialog(order); }}
                                          className="gap-1"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                          Delete
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </TabsContent>

            {/* Resolution History Tab */}
            <TabsContent value="history" className="mt-0">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : resolutionHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-600">No resolution history yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* PDF Export Toolbar for History */}
                  <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-700">Export Resolution Reports</p>
                        <p className="text-xs text-slate-500">Generate PDF reports with full details</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" disabled={exporting} data-testid="history-pdf-export-btn">
                            <Download className="w-4 h-4" />
                            Export PDF Report
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuLabel>Select Report Period</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleExportHistoryPDF('today')} data-testid="export-today-pdf">
                            <Calendar className="w-4 h-4 mr-2 text-green-600" />
                            <div className="flex flex-col">
                              <span>Today&apos;s Report</span>
                              <span className="text-xs text-slate-500">{getDateRangeLabel('today')}</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportHistoryPDF('weekly')} data-testid="export-weekly-pdf">
                            <CalendarDays className="w-4 h-4 mr-2 text-blue-600" />
                            <div className="flex flex-col">
                              <span>Weekly Report</span>
                              <span className="text-xs text-slate-500">Last 7 days</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExportHistoryPDF('monthly')} data-testid="export-monthly-pdf">
                            <CalendarRange className="w-4 h-4 mr-2 text-purple-600" />
                            <div className="flex flex-col">
                              <span>Monthly Report</span>
                              <span className="text-xs text-slate-500">Last 30 days</span>
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  {/* History List */}
                  <div className="space-y-3">
                  {resolutionHistory.slice(0, 50).map((log, idx) => (
                    <div key={idx} className="flex items-start gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">{log.username}</span>
                          {" "}
                          {typeof log.details === "object" && log.details !== null
                            ? JSON.stringify(log.details)
                            : (log.details || "")}
                        </p>
                        {/* Show product details if available */}
                        {log.data_accessed?.products_resolved && log.data_accessed.products_resolved.length > 0 && (
                          <div className="mt-2 text-xs bg-white rounded p-2 border">
                            <p className="font-medium text-slate-700 mb-1">Products Resolved:</p>
                            {log.data_accessed.products_resolved.map((p, i) => (
                              <div key={i} className="flex items-center gap-2 text-slate-600">
                                <span className="text-green-600">✓</span>
                                <span>{p.product}</span>
                                <span className="text-slate-400">→</span>
                                <span className="font-medium">{p.matched_to}</span>
                                <span className="text-slate-400">(₹{p.price} × {p.qty} = ₹{p.total})</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Show resolution details for edit_product */}
                        {log.data_accessed?.resolution?.found_product && (
                          <div className="mt-2 text-xs bg-white rounded p-2 border">
                            <span className="text-green-600">✓</span>
                            <span className="ml-1">Matched to: <span className="font-medium">{log.data_accessed.resolution.found_product}</span></span>
                            {log.data_accessed.resolution.new_price && (
                              <span className="text-slate-500 ml-2">(₹{log.data_accessed.resolution.new_price})</span>
                            )}
                          </div>
                        )}
                        {log.data_accessed?.resolution?.added_new_product && (
                          <div className="mt-2 text-xs bg-blue-50 rounded p-2 border border-blue-200">
                            <span className="text-blue-600">+ Added new product to price list</span>
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              )}
            </TabsContent>

            {/* Overview - already rendered above */}
            <TabsContent value="overview" className="mt-0">
              {/* Content rendered in the cards above */}
            </TabsContent>

            {/* Missing Tracking Tab */}
            <TabsContent value="missing_tracking" className="mt-0">
              <div className="space-y-4">
                {/* Header with filters */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-rose-800 text-sm sm:text-base">Parcels Without Tracking Numbers</h3>
                    <p className="text-xs text-rose-600 mt-0.5">
                      {missingTrackingTotal} parcels have no tracking — invisible in Sales, Ledger & Financial Overview.
                      Assign tracking or delete invalid entries.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                      className="border rounded px-2 py-1 text-xs sm:text-sm flex-1 sm:flex-initial sm:w-40"
                      data-testid="missing-tracking-client-filter"
                      value={missingTrackingFilter}
                      onChange={(e) => { setMissingTrackingFilter(e.target.value); setMissingTrackingPage(1); }}
                    >
                      <option value="">All Clients</option>
                      {(dashboard?.missing_tracking_by_client || []).map((c) => (
                        <option key={c.client_code} value={c.client_code}>{c.client_code} ({c.count})</option>
                      ))}
                    </select>
                    {selectedMissingItems.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        data-testid="delete-selected-missing-btn"
                        onClick={() => setDeleteMissingDialog(true)}
                        className="text-xs"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete ({selectedMissingItems.length})
                      </Button>
                    )}
                  </div>
                </div>

                {/* Client-wise summary cards */}
                {!missingTrackingFilter && dashboard?.missing_tracking_by_client?.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {dashboard.missing_tracking_by_client.slice(0, 10).map((c, i) => (
                      <div
                        key={i}
                        className="bg-white border rounded-lg p-2 sm:p-3 cursor-pointer hover:border-rose-400 hover:shadow-sm transition-all"
                        onClick={() => { setMissingTrackingFilter(c.client_code); setMissingTrackingPage(1); }}
                      >
                        <p className="font-semibold text-xs truncate">{c.client_code}</p>
                        <p className="text-lg font-bold text-rose-600">{c.count}</p>
                        <p className="text-[10px] text-slate-500">Cost: ₹{(c.total_cost || 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Table */}
                {missingTrackingLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    <p className="text-sm text-slate-500 mt-2">Loading...</p>
                  </div>
                ) : missingTrackingParcels.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <CheckCircle className="w-10 h-10 mx-auto text-green-400 mb-2" />
                    <p>No parcels without tracking!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left">
                          <th className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={selectedMissingItems.length === missingTrackingParcels.length && missingTrackingParcels.length > 0}
                              onChange={(e) => {
                                setSelectedMissingItems(e.target.checked ? [...missingTrackingParcels] : []);
                              }}
                              className="rounded"
                            />
                          </th>
                          <th className="px-2 py-2">Client</th>
                          <th className="px-2 py-2">Customer</th>
                          <th className="px-2 py-2">Date</th>
                          <th className="px-2 py-2">Product</th>
                          <th className="px-2 py-2 text-right">Cost</th>
                          <th className="px-2 py-2">Country</th>
                          <th className="px-2 py-2">Sheet</th>
                          <th className="px-2 py-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {missingTrackingParcels.map((parcel, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-2 py-1.5">
                              <input
                                type="checkbox"
                                checked={selectedMissingItems.some(s => s.record_id === parcel.record_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedMissingItems(prev => [...prev, parcel]);
                                  } else {
                                    setSelectedMissingItems(prev => prev.filter(s => s.record_id !== parcel.record_id));
                                  }
                                }}
                                className="rounded"
                              />
                            </td>
                            <td className="px-2 py-1.5 font-medium truncate max-w-[80px]">{parcel.client_code || '-'}</td>
                            <td className="px-2 py-1.5 truncate max-w-[100px]">{parcel.customer_name || '-'}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{formatDMY(parcel.order_date)}</td>
                            <td className="px-2 py-1.5 truncate max-w-[120px]">{parcel.product || '-'}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{parcel.total_cost || 0}</td>
                            <td className="px-2 py-1.5 truncate max-w-[80px]">{parcel.country || '-'}</td>
                            <td className="px-2 py-1.5 truncate max-w-[80px] text-[10px]">{parcel.sheet_name || '-'}</td>
                            <td className="px-2 py-1.5 text-center">
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`assign-tracking-${idx}`}
                                className="h-6 text-[10px] sm:text-xs px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                onClick={() => {
                                  setEditTrackingRecord(parcel);
                                  setNewTrackingValue("");
                                  setEditTrackingDialog(true);
                                }}
                              >
                                + Tracking
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {missingTrackingTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <span className="text-xs text-slate-500">
                      Page {missingTrackingPage} of {missingTrackingTotalPages} ({missingTrackingTotal} total)
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={missingTrackingPage <= 1}
                        onClick={() => setMissingTrackingPage(p => p - 1)}
                        className="h-7 text-xs"
                      >
                        Prev
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={missingTrackingPage >= missingTrackingTotalPages}
                        onClick={() => setMissingTrackingPage(p => p + 1)}
                        className="h-7 text-xs"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Pagination */}
            {(activeTab === "costing" || activeTab === "sales" || activeTab === "unassigned") && totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-slate-500">
                  Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalErrors)} of {totalErrors}
                </span>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">Page {currentPage} of {totalPages}</span>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Resolution Dialog */}
      <Dialog open={resolveDialog} onOpenChange={setResolveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resolutionType === "edit_product" && <><Edit className="w-5 h-5" /> Edit Product</>}
              {resolutionType === "assign_client" && <><UserPlus className="w-5 h-5" /> Assign Client</>}
              {resolutionType === "assign_country" && <><Globe className="w-5 h-5 text-blue-600" /> Assign Country</>}
              {resolutionType === "manual_price" && <><DollarSign className="w-5 h-5" /> Manual Price</>}
              {resolutionType === "assign_service" && <><Package className="w-5 h-5 text-orange-600" /> Fix Shipping Service</>}
              {resolutionType === "mark_ignored" && <><XCircle className="w-5 h-5 text-red-500" /> Mark as Ignored</>}
              {resolutionType === "recalculate" && <><Calculator className="w-5 h-5" /> Recalculate</>}
            </DialogTitle>
            <DialogDescription>
              {selectedError && (
                <span className="font-mono text-xs">
                  Tracking: {selectedError.tracking || selectedError.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {resolutionType === "edit_product" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">New Product Name</label>
                  <Input 
                    value={newValue} 
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Enter correct product name"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Will lookup price from product list automatically</p>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={addToProducts}
                      onChange={(e) => setAddToProducts(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-blue-700">
                      Add this product to Price List
                    </span>
                  </label>
                  {addToProducts && (
                    <div className="mt-3">
                      <label className="text-sm font-medium">Unit Price (₹)</label>
                      <Input 
                        type="number"
                        value={productPrice} 
                        onChange={(e) => setProductPrice(e.target.value)}
                        placeholder="Enter unit price for this product"
                        className="mt-1"
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        This will add the product to your Products panel for future use
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {resolutionType === "assign_client" && (
              <div>
                <label className="text-sm font-medium">Client Code</label>
                <Select value={newValue} onValueChange={setNewValue}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientList.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {resolutionType === "assign_country" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Select Country</label>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger className="mt-1" data-testid="country-select-trigger">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {countryList.length > 0 ? (
                        countryList.map(country => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-slate-500">No countries available</div>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Select the correct destination country for this shipment
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <p className="font-medium">What happens next:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>The country will be assigned to this order</li>
                    <li>Shipping cost will be recalculated based on the shipping rates</li>
                    <li>The &quot;Country Not Detected&quot; error will be resolved</li>
                  </ul>
                </div>
              </div>
            )}
            
            {resolutionType === "manual_price" && (
              <>
                <div>
                  <label className="text-sm font-medium">Medicine Cost (₹)</label>
                  <Input 
                    type="number"
                    value={manualPrice} 
                    onChange={(e) => setManualPrice(e.target.value)}
                    placeholder="Enter medicine cost"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Shipping Cost (₹)</label>
                  <Input 
                    type="number"
                    value={manualShipping} 
                    onChange={(e) => setManualShipping(e.target.value)}
                    placeholder="Enter shipping cost"
                    className="mt-1"
                  />
                </div>
              </>
            )}
            
            {resolutionType === "assign_service" && (
              <div className="space-y-4">
                {selectedError && (
                  <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                    <div><span className="font-medium">Weight:</span> {selectedError.gross_weight || selectedError.packing_weight || selectedError.weight || 0}g</div>
                    <div><span className="font-medium">Detected Country:</span> {selectedError.detected_country || selectedError.country || <span className="text-red-500">Not detected</span>}</div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium">Shipping Service</label>
                  <Select value={selectedService} onValueChange={(val) => {
                    setSelectedService(val);
                    const svc = shippingServices.find(s => s.service === val);
                    setServiceCountries(svc?.countries || []);
                    setShippingRateLookup(null);
                    // Auto-lookup rate if country is already set
                    if (selectedCountry && val) {
                      const weight = selectedError?.gross_weight || selectedError?.packing_weight || selectedError?.weight || 0;
                      lookupShippingRate(val, selectedCountry, weight);
                    }
                  }}>
                    <SelectTrigger className="mt-1" data-testid="service-select">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {shippingServices.map(s => (
                        <SelectItem key={s.service} value={s.service}>
                          {s.service} ({s.countries?.length || 0} countries)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Country</label>
                  <Select value={selectedCountry} onValueChange={(val) => {
                    setSelectedCountry(val);
                    if (selectedService && val) {
                      const weight = selectedError?.gross_weight || selectedError?.packing_weight || selectedError?.weight || 0;
                      lookupShippingRate(selectedService, val, weight);
                    }
                  }}>
                    <SelectTrigger className="mt-1" data-testid="service-country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {serviceCountries.length > 0 ? (
                        serviceCountries.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-slate-500">Select a service first</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                {lookingUpRate && (
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" /> Looking up rate...
                  </div>
                )}
                
                {shippingRateLookup && (
                  <div className={`p-3 rounded-lg text-sm ${shippingRateLookup.found ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {shippingRateLookup.found ? (
                      <>
                        <p className="font-medium">Rate Found!</p>
                        {shippingRateLookup.matched_rate ? (
                          <p>Weight range: {shippingRateLookup.matched_rate.weight_range} → ₹{shippingRateLookup.matched_rate.cost}</p>
                        ) : (
                          <p className="text-orange-600">No exact weight match. Closest rate will be applied.</p>
                        )}
                      </>
                    ) : (
                      <p>No shipping rate found for {selectedService} to {selectedCountry}. You may need to add rates in Shipping Prices first.</p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {resolutionType === "mark_ignored" && (
              <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                This will mark the order as ignored and remove all errors. Use for cancelled orders or test data.
              </div>
            )}
            
            {resolutionType === "recalculate" && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                This will attempt to recalculate prices by re-looking up products in the price list.
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about this resolution..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog(false)}>Cancel</Button>
            <Button onClick={handleResolve} disabled={resolving}>
              {resolving ? "Resolving..." : "Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Assign Client</DialogTitle>
            <DialogDescription>
              Assign client code to {selectedItems.length} selected orders
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Select value={bulkClientCode} onValueChange={setBulkClientCode}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clientList.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.code} - {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign} disabled={resolving || !bulkClientCode}>
              {resolving ? "Assigning..." : `Assign to ${selectedItems.length} Orders`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={(open) => { setDeleteDialog(open); if (!open) { setDeletePassword(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Delete Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {recordToDelete && (
            <div className="py-2 space-y-3">
              <div className="p-3 bg-red-50 rounded-lg text-sm">
                <p><span className="font-medium">Tracking:</span> {recordToDelete.tracking || "No tracking"}</p>
                <p><span className="font-medium">Product:</span> {recordToDelete.second_name || recordToDelete.product || "-"}</p>
                <p><span className="font-medium">Client:</span> {recordToDelete.client_code || "-"}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Enter your login password to confirm</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter password"
                  className="mt-1"
                  data-testid="delete-password-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && deletePassword.trim()) handleDeleteRecord(); }}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteRecord} disabled={deleting || !deletePassword.trim()} data-testid="confirm-delete-btn">
              {deleting ? "Deleting..." : "Delete Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Record Dialog */}
      <Dialog open={editRecordDialog} onOpenChange={setEditRecordDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Pencil className="w-5 h-5" /> Edit Record
            </DialogTitle>
            <DialogDescription>
              Modify record fields directly without re-uploading the sheet.
              {recordToEdit && <span className="block font-mono text-xs mt-1">Tracking: {recordToEdit.tracking}</span>}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Gross Weight (g)</label>
                <Input 
                  type="number"
                  value={editFields.gross_weight} 
                  onChange={(e) => setEditFields(prev => ({...prev, gross_weight: e.target.value}))}
                  placeholder="e.g., 471"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-orange-700">Product Weight per strip (g)</label>
                <Input 
                  type="number"
                  value={editFields.product_weight} 
                  onChange={(e) => setEditFields(prev => ({...prev, product_weight: e.target.value}))}
                  placeholder="e.g., 8"
                  className="mt-1 border-orange-300 focus:border-orange-500"
                  data-testid="edit-product-weight"
                />
                {editFields.product_weight && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="saveToProductDb"
                      checked={editFields.save_to_product_db}
                      onChange={(e) => setEditFields(prev => ({...prev, save_to_product_db: e.target.checked}))}
                      className="w-3.5 h-3.5 rounded"
                      data-testid="save-to-product-db"
                    />
                    <label htmlFor="saveToProductDb" className="text-xs text-orange-600 font-medium">
                      Save weight to Product Prices DB
                    </label>
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Quantity</label>
                <Input 
                  type="number"
                  value={editFields.qty} 
                  onChange={(e) => setEditFields(prev => ({...prev, qty: e.target.value}))}
                  placeholder="e.g., 36"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Product Name</label>
                <Input 
                  value={editFields.second_name} 
                  onChange={(e) => setEditFields(prev => ({...prev, second_name: e.target.value}))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Medicine Cost (₹)</label>
                <Input 
                  type="number"
                  value={editFields.medicine_cost} 
                  onChange={(e) => setEditFields(prev => ({...prev, medicine_cost: e.target.value}))}
                  placeholder="e.g., 6320.16"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Shipping Cost (₹)</label>
                <Input 
                  type="number"
                  value={editFields.shipping_cost} 
                  onChange={(e) => setEditFields(prev => ({...prev, shipping_cost: e.target.value}))}
                  placeholder="e.g., 2162"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Order Date</label>
                <Input 
                  value={formatDMY(editFields.order_date)} 
                  onChange={(e) => setEditFields(prev => ({...prev, order_date: e.target.value}))}
                  placeholder="e.g., 010925 (DDMMYY)"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Client Code</label>
                <Input 
                  value={editFields.client_code} 
                  onChange={(e) => setEditFields(prev => ({...prev, client_code: e.target.value}))}
                  placeholder="e.g., ALCHEMIST"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Country</label>
                <Input 
                  value={editFields.country} 
                  onChange={(e) => setEditFields(prev => ({...prev, country: e.target.value}))}
                  placeholder="e.g., USA"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
              <p className="font-medium">Note:</p>
              <p>Only fill the fields you want to change. Leave others empty to keep current values.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRecordDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRecord} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to Order Dialog */}
      <Dialog open={linkOrderDialog} onOpenChange={setLinkOrderDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Link2 className="w-5 h-5" /> Link Sale to Order
            </DialogTitle>
            <DialogDescription>
              Search for an existing shipment order to link with this sale.
              {saleToUpdate && (
                <span className="block font-mono text-xs mt-1">
                  Sale Tracking: {saleToUpdate.tracking || saleToUpdate.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Search Input */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by tracking number, customer name..."
                  value={orderSearchTerm}
                  onChange={(e) => setOrderSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchOrdersToLink()}
                  className="pl-10"
                />
              </div>
              <Button onClick={searchOrdersToLink} disabled={searchingOrders}>
                {searchingOrders ? "Searching..." : "Search"}
              </Button>
            </div>
            
            {/* Search Results */}
            {orderSearchResults.length > 0 && (
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Tracking</th>
                      <th className="p-2 text-left">Client</th>
                      <th className="p-2 text-left">Product</th>
                      <th className="p-2 text-right">Cost</th>
                      <th className="p-2 text-left">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderSearchResults.map((order, idx) => (
                      <tr 
                        key={idx} 
                        className={`border-b cursor-pointer hover:bg-blue-50 ${selectedOrderToLink?.tracking === order.tracking ? 'bg-blue-100' : ''}`}
                        onClick={() => setSelectedOrderToLink(order)}
                      >
                        <td className="p-2">
                          <input 
                            type="radio" 
                            checked={selectedOrderToLink?.tracking === order.tracking}
                            onChange={() => setSelectedOrderToLink(order)}
                          />
                        </td>
                        <td className="p-2 font-mono text-xs">{order.tracking}</td>
                        <td className="p-2">{order.client_code || "-"}</td>
                        <td className="p-2 truncate max-w-[150px]">{order.second_name || order.product || "-"}</td>
                        <td className="p-2 text-right">₹{(order.total_cost || 0).toLocaleString()}</td>
                        <td className="p-2 text-xs">{formatDMY(order.order_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {orderSearchResults.length === 0 && orderSearchTerm && !searchingOrders && (
              <div className="text-center py-4 text-slate-500">
                No orders found. Try a different search term.
              </div>
            )}
            
            {/* Selected Order Preview */}
            {selectedOrderToLink && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-700 mb-2">Selected Order:</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-600">Tracking:</span> {selectedOrderToLink.tracking}</div>
                  <div><span className="text-slate-600">Client:</span> {selectedOrderToLink.client_code || "-"}</div>
                  <div><span className="text-slate-600">Product:</span> {selectedOrderToLink.second_name || selectedOrderToLink.product || "-"}</div>
                  <div><span className="text-slate-600">Total Cost:</span> ₹{(selectedOrderToLink.total_cost || 0).toLocaleString()}</div>
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about this link..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOrderDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleLinkToOrder} 
              disabled={resolving || !selectedOrderToLink}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {resolving ? "Linking..." : "Link to Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Cost Entry Dialog */}
      <Dialog open={manualCostDialog} onOpenChange={setManualCostDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CircleDollarSign className="w-5 h-5" /> Manual Cost Entry
            </DialogTitle>
            <DialogDescription>
              Enter the cost price manually for this sale.
              {saleToUpdate && (
                <span className="block font-mono text-xs mt-1">
                  Sale Tracking: {saleToUpdate.tracking || saleToUpdate.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Sale Info */}
            {saleToUpdate && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-600">Client:</span> {saleToUpdate.client_code || "-"}</div>
                  <div><span className="text-slate-600">Customer:</span> {saleToUpdate.customer_name || saleToUpdate.name || "-"}</div>
                  <div><span className="text-slate-600">Date:</span> {formatDMY(saleToUpdate.sale_date)}</div>
                  {isAdmin && (
                    <div><span className="text-slate-600">Sell Price:</span> ₹{(saleToUpdate.sell_price || 0).toLocaleString()}</div>
                  )}
                </div>
              </div>
            )}
            
            {/* Cost Input */}
            <div>
              <label className="text-sm font-medium">Cost Price (₹)</label>
              <Input 
                type="number"
                value={manualCostValue} 
                onChange={(e) => setManualCostValue(e.target.value)}
                placeholder="Enter cost price"
                className="mt-1"
              />
              {isAdmin && saleToUpdate?.sell_price && manualCostValue && (
                <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                  <span className="text-green-700 font-medium">
                    Calculated Profit: ₹{((saleToUpdate.sell_price || 0) - parseFloat(manualCostValue || 0)).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about this cost entry..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <p className="font-medium">Note:</p>
              <p>This will update the cost price for this sale and resolve the &quot;Missing Cost&quot; error.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualCostDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleManualCostEntry} 
              disabled={resolving || !manualCostValue}
              className="bg-green-600 hover:bg-green-700"
            >
              {resolving ? "Saving..." : "Save Cost"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sell Price Dialog - for fixing negative profit */}
      <Dialog open={editSellPriceDialog} onOpenChange={setEditSellPriceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <DollarSign className="w-5 h-5" /> Edit Sell Price
            </DialogTitle>
            <DialogDescription>
              Update the sell price to fix the negative profit issue.
              {selectedError && (
                <span className="block font-mono text-xs mt-1">
                  Sale Tracking: {selectedError.tracking || selectedError.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Sale Info */}
            {selectedError && (
              <div className="p-3 bg-red-50 rounded-lg text-sm border border-red-200">
                <p className="font-medium text-red-700 mb-2">Current Values (Loss):</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-600">Client:</span> {selectedError.client_code || "-"}</div>
                  <div><span className="text-slate-600">Customer:</span> {selectedError.customer_name || selectedError.name || "-"}</div>
                  <div><span className="text-slate-600">Current Sell:</span> ₹{(selectedError.sell_price || 0).toLocaleString()}</div>
                  <div><span className="text-slate-600">Cost:</span> ₹{(selectedError.cost_price || 0).toLocaleString()}</div>
                  <div className="col-span-2">
                    <span className="text-red-600 font-medium">Current Profit: ₹{(selectedError.profit || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* New Sell Price Input */}
            <div>
              <label className="text-sm font-medium">New Sell Price (₹)</label>
              <Input 
                type="number"
                value={newSellPrice} 
                onChange={(e) => setNewSellPrice(e.target.value)}
                placeholder="Enter new sell price"
                className="mt-1"
                data-testid="new-sell-price-input"
              />
              {selectedError?.cost_price && newSellPrice && (
                <div className={`mt-2 p-2 rounded text-sm ${
                  parseFloat(newSellPrice) - (selectedError.cost_price || 0) >= 0 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  <span className="font-medium">
                    New Profit: ₹{(parseFloat(newSellPrice || 0) - (selectedError.cost_price || 0)).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about why the price was changed..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">What happens:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>The sell price will be updated</li>
                <li>Profit will be recalculated (Sell - Cost)</li>
                <li>The error will be resolved if profit becomes positive</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditSellPriceDialog(false); setSelectedError(null); setNewSellPrice(""); setResolutionNotes(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSellPrice} 
              disabled={resolving || !newSellPrice}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-sell-price-btn"
            >
              {resolving ? "Saving..." : "Save New Price"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Date Dialog */}
      <Dialog open={editSaleDateDialog} onOpenChange={setEditSaleDateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Calendar className="w-5 h-5" /> Edit Sale Date
            </DialogTitle>
            <DialogDescription>
              Update the sale date for this record.
              {selectedError && (
                <span className="block font-mono text-xs mt-1">
                  Sale Tracking: {selectedError.tracking || selectedError.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Sale Info */}
            {selectedError && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm border">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-600">Client:</span> {selectedError.client_code || "-"}</div>
                  <div><span className="text-slate-600">Customer:</span> {selectedError.customer_name || selectedError.name || "-"}</div>
                  <div className="col-span-2">
                    <span className="text-slate-600">Current Date:</span> 
                    <span className="ml-2 font-medium">{formatDMY(selectedError.sale_date)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* New Date Input */}
            <div>
              <label className="text-sm font-medium">New Sale Date</label>
              <div className="flex gap-2 mt-1">
                <DMYDateInput value={newSaleDate} onChange={(e) => setNewSaleDate(e.target.value)} className="flex-1" data-testid="new-sale-date-input" />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchDateFromShipmentOrder}
                  disabled={fetchingOrderDate}
                  title="Fetch from Shipment Order"
                  className="whitespace-nowrap"
                >
                  {fetchingOrderDate ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      From Order
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Click "From Order" to fetch the date from the linked shipment order
              </p>
            </div>
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about why the date was changed..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">💡 Tip:</p>
              <p className="mt-1">
                Use "From Order" to automatically fetch the correct date from the shipment costing record. 
                This ensures consistency between sales and costing data.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditSaleDateDialog(false); setSelectedError(null); setNewSaleDate(""); setResolutionNotes(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSaleDate} 
              disabled={resolving || !newSaleDate}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-sale-date-btn"
            >
              {resolving ? "Saving..." : "Save Date"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quantity Dialog */}
      <Dialog open={editQuantityDialog} onOpenChange={(open) => { setEditQuantityDialog(open); if (!open) { setSelectedError(null); setNewQuantity(""); setResolutionNotes(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Hash className="w-5 h-5" /> Edit Quantity
            </DialogTitle>
            <DialogDescription>
              Change the quantity for this parcel. Costs will be recalculated across all panels.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedError && (
              <div className="p-3 bg-amber-50 rounded-lg text-sm space-y-1">
                <p><span className="font-medium">Tracking:</span> {selectedError.tracking || selectedError.tracking_number}</p>
                <p><span className="font-medium">Client:</span> {selectedError.client_code}</p>
                <p><span className="font-medium">Current Cost:</span> ₹{selectedError.cost_price?.toLocaleString('en-IN') || selectedError.total_cost?.toLocaleString('en-IN') || '0'}</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium">New Quantity</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={newQuantity}
                onChange={(e) => setNewQuantity(e.target.value)}
                placeholder="Enter quantity"
                className="mt-1"
                data-testid="edit-quantity-input"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Notes (optional)</Label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Reason for quantity change..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p>Changing quantity will recalculate medicine cost and update Shipment Costing, Sales, and Ledger panels.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditQuantityDialog(false); setSelectedError(null); setNewQuantity(""); setResolutionNotes(""); }}>
              Cancel
            </Button>
            <Button
              onClick={handleEditQuantity}
              disabled={resolving || !newQuantity || parseInt(newQuantity) < 1}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-quantity-btn"
            >
              {resolving ? "Updating..." : "Update Quantity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Ignore Sale Error Dialog */}
      <Dialog open={ignoreDialog} onOpenChange={setIgnoreDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Ban className="w-5 h-5" /> Ignore Sale Error
            </DialogTitle>
            <DialogDescription>
              Mark this sale error as ignored. It will be removed from the error list.
              {selectedError && (
                <span className="block font-mono text-xs mt-1">
                  Sale Tracking: {selectedError.tracking || selectedError.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Sale Info */}
            {selectedError && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-600">Client:</span> {selectedError.client_code || "-"}</div>
                  <div><span className="text-slate-600">Customer:</span> {selectedError.customer_name || selectedError.name || "-"}</div>
                  <div><span className="text-slate-600">Sell Price:</span> ₹{(selectedError.sell_price || 0).toLocaleString()}</div>
                  <div><span className="text-slate-600">Cost:</span> ₹{selectedError.cost_price ? selectedError.cost_price.toLocaleString() : "Missing"}</div>
                  <div><span className="text-slate-600">Profit:</span> <span className={selectedError.profit < 0 ? 'text-red-600' : ''}> ₹{(selectedError.profit || 0).toLocaleString()}</span></div>
                  <div><span className="text-slate-600">Error Type:</span> {selectedError.error_type || "Unknown"}</div>
                </div>
              </div>
            )}
            
            {/* Ignore Reason */}
            <div>
              <label className="text-sm font-medium">Reason for Ignoring <span className="text-red-500">*</span></label>
              <Select value={ignoreReason} onValueChange={setIgnoreReason}>
                <SelectTrigger className="mt-1" data-testid="ignore-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test_order">Test Order</SelectItem>
                  <SelectItem value="cancelled_order">Cancelled Order</SelectItem>
                  <SelectItem value="refund_processed">Refund Processed</SelectItem>
                  <SelectItem value="duplicate_entry">Duplicate Entry</SelectItem>
                  <SelectItem value="data_error">Data Entry Error</SelectItem>
                  <SelectItem value="intentional_loss">Intentional Loss (Promo/Sample)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Additional Notes */}
            <div>
              <label className="text-sm font-medium">Additional Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add any additional notes..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-700 border border-amber-200">
              <p className="font-medium">⚠️ Warning:</p>
              <p>This will mark the error as intentionally ignored. It will still be counted but won&apos;t appear in the error list.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIgnoreDialog(false); setSelectedError(null); setIgnoreReason(""); setResolutionNotes(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleIgnoreSale} 
              disabled={resolving || !ignoreReason}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-ignore-btn"
            >
              {resolving ? "Saving..." : "Ignore Error"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Sale to Sale Dialog */}
      <Dialog open={linkSaleDialog} onOpenChange={setLinkSaleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <Shuffle className="w-5 h-5" /> Link to Another Sale
            </DialogTitle>
            <DialogDescription>
              Link this sale to another sale record for merging.
              {selectedError && (
                <span className="block font-mono text-xs mt-1">
                  Source Tracking: {selectedError.tracking || selectedError.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Current Sale Info */}
            {selectedError && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium text-slate-700 mb-2">Current Sale:</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-600">Client:</span> {selectedError.client_code || "-"}</div>
                  <div><span className="text-slate-600">Sell:</span> ₹{(selectedError.sell_price || 0).toLocaleString()}</div>
                </div>
              </div>
            )}
            
            {/* Target Tracking Input */}
            <div>
              <label className="text-sm font-medium">Target Tracking Number</label>
              <Input 
                value={linkToTracking} 
                onChange={(e) => setLinkToTracking(e.target.value)}
                placeholder="Enter tracking number to link to"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Enter the tracking number of the sale you want to merge with</p>
            </div>
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea 
                value={resolutionNotes} 
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Add notes about this link..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
              <p className="font-medium">What happens:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>The sell amounts will be merged</li>
                <li>Cost from target sale will be used</li>
                <li>This sale will be marked as linked</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setLinkSaleDialog(false); setSelectedError(null); setLinkToTracking(""); setResolutionNotes(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleLinkSaleToSale} 
              disabled={resolving || !linkToTracking}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {resolving ? "Linking..." : "Link Sales"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Narration Logic Dialog */}
      <Dialog open={narrationLogicDialog} onOpenChange={setNarrationLogicDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-600">
              <FileText className="w-5 h-5" /> Add Narration Logic
            </DialogTitle>
            <DialogDescription>
              Add a narration logic entry for this sale record.
              {narrationSale && (
                <span className="block font-mono text-xs mt-1">
                  Tracking: {narrationSale.tracking || narrationSale.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Narration Text
              </label>
              <Textarea
                placeholder="Enter narration logic details..."
                value={narrationText}
                onChange={(e) => setNarrationText(e.target.value)}
                className="min-h-[100px]"
                data-testid="narration-logic-text"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Resolution Notes (Optional)
              </label>
              <Textarea
                placeholder="Additional notes about this narration..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                className="min-h-[60px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { 
              setNarrationLogicDialog(false); 
              setNarrationSale(null); 
              setNarrationText(""); 
              setResolutionNotes(""); 
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleAddNarration("logic")}
              disabled={!narrationText.trim() || resolving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-narration-logic-btn"
            >
              {resolving ? "Saving..." : "Save Narration Logic"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Narration Manual Dialog */}
      <Dialog open={narrationManualDialog} onOpenChange={setNarrationManualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <Edit className="w-5 h-5" /> Add Manual Narration
            </DialogTitle>
            <DialogDescription>
              Add a manual narration entry for this sale record.
              {narrationSale && (
                <span className="block font-mono text-xs mt-1">
                  Tracking: {narrationSale.tracking || narrationSale.tracking_number}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Manual Narration Text
              </label>
              <Textarea
                placeholder="Enter manual narration details..."
                value={narrationText}
                onChange={(e) => setNarrationText(e.target.value)}
                className="min-h-[100px]"
                data-testid="narration-manual-text"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Resolution Notes (Optional)
              </label>
              <Textarea
                placeholder="Additional notes about this narration..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                className="min-h-[60px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { 
              setNarrationManualDialog(false); 
              setNarrationSale(null); 
              setNarrationText(""); 
              setResolutionNotes(""); 
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleAddNarration("manual")}
              disabled={!narrationText.trim() || resolving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-narration-manual-btn"
            >
              {resolving ? "Saving..." : "Save Manual Narration"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History PDF Export Choice Dialog */}
      <Dialog open={historyPdfDialog} onOpenChange={setHistoryPdfDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Export History Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              How would you like to view the <strong>{historyPdfReportType === 'today' ? "Today's" : historyPdfReportType === 'weekly' ? 'Weekly' : 'Monthly'}</strong> report?
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-24 flex flex-col items-center justify-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                onClick={handleHistoryScreenPreview}
                data-testid="history-screen-preview-btn"
              >
                <Search className="w-8 h-8 text-blue-600" />
                <span className="text-sm font-medium">Preview on Screen</span>
              </Button>
              
              <Button
                variant="outline"
                className="h-24 flex flex-col items-center justify-center gap-2 hover:bg-green-50 hover:border-green-300"
                onClick={handleHistoryDirectPDF}
                disabled={exporting}
                data-testid="history-direct-pdf-btn"
              >
                <Download className="w-8 h-8 text-green-600" />
                <span className="text-sm font-medium">
                  {exporting ? "Generating..." : "Download PDF"}
                </span>
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHistoryPdfDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History PDF Screen Preview Modal */}
      <Dialog open={historyPdfPreview} onOpenChange={setHistoryPdfPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-bold text-blue-800 flex items-center gap-2">
                  <FileText className="w-6 h-6" />
                  Error Resolution Report - {historyPdfPreviewData?.reportTypeLabel}
                </DialogTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Generated: {historyPdfPreviewData?.generatedAt}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    window.print();
                  }}
                  variant="outline"
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </Button>
                <Button
                  onClick={() => {
                    generateHistoryPDF(resolutionHistory, historyPdfReportType);
                    toast.success("PDF downloaded!");
                  }}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          {/* Preview Content */}
          <div className="flex-1 overflow-auto bg-slate-100 p-4 rounded-lg mt-4">
            {historyPdfPreviewData && (
              <div className="bg-white shadow-lg rounded-lg mx-auto max-w-4xl">
                {/* Report Header */}
                <div className="bg-blue-600 text-white py-6 px-8 rounded-t-lg">
                  <h1 className="text-2xl font-bold mb-1">EAC</h1>
                  <h2 className="text-lg opacity-90">Error Resolution Report</h2>
                  <p className="text-sm opacity-75 mt-2">{historyPdfPreviewData.reportTypeLabel}</p>
                </div>
                
                {/* Summary Cards */}
                <div className="flex justify-center gap-6 py-6 px-8 bg-slate-50 border-b">
                  <div className="bg-white border rounded-lg px-8 py-4 text-center shadow-sm">
                    <p className="text-3xl font-bold text-blue-600">{historyPdfPreviewData.totalResolutions}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Total Resolutions</p>
                  </div>
                  <div className="bg-white border rounded-lg px-8 py-4 text-center shadow-sm">
                    <p className="text-3xl font-bold text-green-600">{historyPdfPreviewData.uniqueUsers}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Active Users</p>
                  </div>
                  <div className="bg-white border rounded-lg px-8 py-4 text-center shadow-sm">
                    <p className="text-3xl font-bold text-purple-600">{Object.keys(historyPdfPreviewData.resolutionTypes).length}</p>
                    <p className="text-xs text-slate-500 uppercase mt-1">Resolution Types</p>
                  </div>
                </div>
                
                {/* Resolution Types Breakdown */}
                <div className="px-8 py-4 border-b">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Resolution Types Breakdown:</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(historyPdfPreviewData.resolutionTypes).map(([type, count]) => (
                      <span key={type} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                        {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {count}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Detailed Resolution List - Full Details */}
                <div className="p-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-4">Complete Resolution History:</h3>
                  <div className="space-y-4">
                    {historyPdfPreviewData.filteredData.map((item, idx) => {
                      // Build ALL details from the resolution - check multiple possible field locations
                      const sourceTracking = item.data_accessed?.source_tracking || 
                                            item.data_accessed?.tracking || 
                                            item.data_accessed?.source || '-';
                      const targetTracking = item.data_accessed?.target_tracking || 
                                            item.data_accessed?.target || 
                                            item.data_accessed?.linked_to || '';
                      const actionType = item.data_accessed?.resolution_type || item.action || 'unknown';
                      
                      return (
                        <div key={idx} className="border rounded-lg overflow-hidden">
                          {/* Header Row */}
                          <div className="bg-slate-100 px-4 py-2 flex items-center justify-between border-b">
                            <div className="flex items-center gap-4">
                              <span className="text-xs text-slate-500">
                                {new Date(item.timestamp).toLocaleString()}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                actionType.includes('narration') ? 'bg-purple-100 text-purple-700' :
                                actionType.includes('link') ? 'bg-blue-100 text-blue-700' :
                                actionType.includes('ignore') ? 'bg-amber-100 text-amber-700' :
                                actionType.includes('cost') ? 'bg-green-100 text-green-700' :
                                actionType.includes('price') ? 'bg-emerald-100 text-emerald-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {actionType.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-slate-700">
                              {item.username || 'Unknown'}
                              {item.role && <span className="text-xs text-slate-400 ml-1">({item.role})</span>}
                            </span>
                          </div>
                          
                          {/* Details Body */}
                          <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                            {/* Source Tracking Info */}
                            <div>
                              <p className="text-xs text-slate-500 uppercase font-medium mb-1">
                                {targetTracking ? 'Source Tracking' : 'Tracking Number'}
                              </p>
                              <p className="font-mono text-blue-600 font-semibold">{sourceTracking}</p>
                            </div>
                            
                            {/* Target/Linked Tracking */}
                            {targetTracking && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Target / Linked To</p>
                                <p className="font-mono text-green-600 font-semibold">{targetTracking}</p>
                              </div>
                            )}
                            
                            {/* Client Code */}
                            {item.data_accessed?.client_code && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Client</p>
                                <p className="font-medium">{item.data_accessed.client_code}</p>
                              </div>
                            )}
                            
                            {/* Cost Assigned */}
                            {item.data_accessed?.cost_assigned !== undefined && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Cost Assigned</p>
                                <p className="font-semibold text-green-700">
                                  ₹{Number(item.data_accessed.cost_assigned || 0).toLocaleString()}
                                </p>
                              </div>
                            )}
                            
                            {/* New Sell Price */}
                            {item.data_accessed?.new_sell_price !== undefined && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Sell Price</p>
                                <p className="font-semibold">₹{Number(item.data_accessed.new_sell_price || 0).toLocaleString()}</p>
                              </div>
                            )}
                            
                            {/* New Profit */}
                            {item.data_accessed?.new_profit !== undefined && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Profit</p>
                                <p className={`font-semibold ${item.data_accessed.new_profit < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  ₹{Number(item.data_accessed.new_profit || 0).toLocaleString()}
                                </p>
                              </div>
                            )}
                            
                            {/* Link Type */}
                            {item.data_accessed?.link_type && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Link Type</p>
                                <p>{item.data_accessed.link_type.replace(/_/g, ' ')}</p>
                              </div>
                            )}
                            
                            {/* Narration Type */}
                            {item.data_accessed?.narration_type && (
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Narration Type</p>
                                <p className="capitalize">{item.data_accessed.narration_type}</p>
                              </div>
                            )}
                            
                            {/* Products Resolved */}
                            {item.data_accessed?.products_resolved?.length > 0 && (
                              <div className="col-span-2">
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Products Resolved</p>
                                <div className="space-y-1">
                                  {item.data_accessed.products_resolved.map((p, i) => (
                                    <p key={i} className="text-sm">
                                      <span className="text-red-500">{p.product}</span>
                                      <span className="mx-2">→</span>
                                      <span className="text-green-600 font-medium">{p.matched_to}</span>
                                    </p>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Ignore Reason */}
                            {item.data_accessed?.ignore_reason && (
                              <div className="col-span-2">
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Ignore Reason</p>
                                <p className="text-amber-700">{item.data_accessed.ignore_reason}</p>
                              </div>
                            )}
                            
                            {/* Narration Text */}
                            {item.data_accessed?.narration_text && (
                              <div className="col-span-2">
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Narration</p>
                                <p className="bg-purple-50 p-2 rounded border border-purple-200 text-purple-800">
                                  {item.data_accessed.narration_text}
                                </p>
                              </div>
                            )}
                            
                            {/* Notes */}
                            {item.data_accessed?.notes && (
                              <div className="col-span-2">
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Notes</p>
                                <p className="text-slate-600 italic">{item.data_accessed.notes}</p>
                              </div>
                            )}
                            
                            {/* Description fallback */}
                            {item.description && !item.data_accessed?.narration_text && !item.data_accessed?.notes && (
                              <div className="col-span-2">
                                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Description</p>
                                <p className="text-slate-600">{item.description}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Footer */}
                <div className="px-6 py-4 bg-slate-100 rounded-b-lg border-t text-xs text-slate-500 flex justify-between">
                  <span>EAC - Error Resolution System</span>
                  <span>Confidential</span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setHistoryPdfPreview(false)}>
              Close Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Costing Export Dialog - Full export with date options */}
      <CostingExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        token={token}
        mode={exportDialogMode}
        totalErrors={totalErrors}
        currentFilters={{
          clientCode: filterClient,
          errorType: filterErrorType,
          search: searchTerm,
          dateFrom,
          dateTo
        }}
      />

      {/* Assign Tracking Dialog */}
      <Dialog open={editTrackingDialog} onOpenChange={(open) => { setEditTrackingDialog(open); if (!open) { setEditTrackingRecord(null); setNewTrackingValue(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm sm:text-base">Assign Tracking Number</DialogTitle>
          </DialogHeader>
          {editTrackingRecord && (
            <div className="space-y-3 py-2">
              <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Client:</span><span className="font-medium">{editTrackingRecord.client_code}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Customer:</span><span className="font-medium">{editTrackingRecord.customer_name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Product:</span><span className="font-medium">{editTrackingRecord.product || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Cost:</span><span className="font-medium">₹{(editTrackingRecord.total_cost || 0).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date:</span><span className="font-medium">{formatDMY(editTrackingRecord.order_date)}</span></div>
              </div>
              <div>
                <Label className="text-xs">Tracking Number</Label>
                <Input
                  value={newTrackingValue}
                  onChange={(e) => setNewTrackingValue(e.target.value)}
                  placeholder="Enter tracking number"
                  data-testid="assign-tracking-input"
                  className="mt-1"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditTrackingDialog(false)}>Cancel</Button>
            <Button
              size="sm"
              data-testid="confirm-assign-tracking-btn"
              onClick={handleAssignTracking}
              disabled={!newTrackingValue.trim() || savingTracking}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingTracking ? "Saving..." : "Assign Tracking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Missing Tracking Parcels Dialog */}
      <Dialog open={deleteMissingDialog} onOpenChange={(open) => { setDeleteMissingDialog(open); if (!open) setDeleteMissingPassword(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 text-sm sm:text-base">
              <Trash2 className="w-5 h-5" />
              Delete Parcels
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                Delete <strong>{selectedMissingItems.length}</strong> parcels without tracking?
              </p>
              <p className="text-xs text-red-600 mt-1">This action cannot be undone.</p>
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input
                type="password"
                value={deleteMissingPassword}
                onChange={(e) => setDeleteMissingPassword(e.target.value)}
                placeholder="Enter password to confirm"
                data-testid="delete-missing-password"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeleteMissingDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              size="sm"
              data-testid="confirm-delete-missing-btn"
              onClick={handleDeleteMissingParcels}
              disabled={!deleteMissingPassword}
            >
              Delete {selectedMissingItems.length} Parcels
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ErrorResolutionPage;
